import React, {useState, useRef, useEffect, useMemo, useCallback} from 'react';
import { createPortal } from 'react-dom';
import {
    Bar,
    Line,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    ComposedChart,
    PieChart,
    Pie,
    Cell
} from 'recharts';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import Globe from 'react-globe.gl';
import { useAuth } from './Auth/AuthContext';
import {
    fetchSummary,
    fetchRealtimeVisitors,
    fetchRealtimeActivity,
    fetchAcquisition,
    fetchGeo,
    fetchTech,
    fetchHost,
    fetchJourney,
    fetchVisitors,
} from '../utils/dashboardApi';
import '../styles/dashboard.css';

const GEO_MAP_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

// Country labels for globe (Google Earth style)
const COUNTRY_LABELS = [
    { name: 'United States', lat: 39.8, lng: -98.5 },
    { name: 'Canada', lat: 56.1, lng: -106.3 },
    { name: 'Brazil', lat: -14.2, lng: -51.9 },
    { name: 'Argentina', lat: -38.4, lng: -63.6 },
    { name: 'Mexico', lat: 23.6, lng: -102.5 },
    { name: 'United Kingdom', lat: 55.4, lng: -3.4 },
    { name: 'France', lat: 46.2, lng: 2.2 },
    { name: 'Germany', lat: 51.2, lng: 10.5 },
    { name: 'Spain', lat: 40.5, lng: -3.7 },
    { name: 'Italy', lat: 41.9, lng: 12.6 },
    { name: 'Russia', lat: 61.5, lng: 105.3 },
    { name: 'China', lat: 35.9, lng: 104.2 },
    { name: 'India', lat: 20.6, lng: 78.9 },
    { name: 'Japan', lat: 36.2, lng: 138.3 },
    { name: 'Australia', lat: -25.3, lng: 133.8 },
    { name: 'South Africa', lat: -30.6, lng: 22.9 },
    { name: 'Egypt', lat: 26.8, lng: 30.8 },
    { name: 'Nigeria', lat: 9.1, lng: 8.7 },
    { name: 'Kenya', lat: -0.02, lng: 37.9 },
    { name: 'Indonesia', lat: -0.8, lng: 113.9 },
    { name: 'Saudi Arabia', lat: 23.9, lng: 45.1 },
    { name: 'Turkey', lat: 39.0, lng: 35.2 },
    { name: 'Poland', lat: 51.9, lng: 19.1 },
    { name: 'Ukraine', lat: 48.4, lng: 31.2 },
    { name: 'Sweden', lat: 60.1, lng: 18.6 },
    { name: 'Norway', lat: 60.5, lng: 8.5 },
    { name: 'Greenland', lat: 71.7, lng: -42.6 },
    { name: 'Iceland', lat: 64.9, lng: -19.0 },
    { name: 'Colombia', lat: 4.6, lng: -74.3 },
    { name: 'Peru', lat: -9.2, lng: -75.0 },
    { name: 'Chile', lat: -35.7, lng: -71.5 },
    { name: 'Venezuela', lat: 6.4, lng: -66.6 },
    { name: 'North Atlantic Ocean', lat: 35.0, lng: -45.0 },
    { name: 'South Atlantic Ocean', lat: -25.0, lng: -25.0 },
    { name: 'Pacific Ocean', lat: 0.0, lng: -160.0 },
    { name: 'Indian Ocean', lat: -20.0, lng: 80.0 }
];
const FLAG_BASE = 'https://purecatamphetamine.github.io/country-flag-icons/3x2';
const GEO_NAME_TO_OUR = {
    'United States of America': 'United States',
    'United Kingdom of Great Britain and Northern Ireland': 'United Kingdom',
    'United Kingdom': 'United Kingdom'
};

/** Max number of records shown in dashboard cards; detail modals show all. */
const CARD_LIST_LIMIT = 8;

// Custom dot for hourly chart - shows user avatar at specific points
function HourlyChartDot(props) {
    const { cx, cy, payload } = props;
    if (cx == null || cy == null || !payload?.user) return null;
    return (
        <g className="dashboard-chart-dot">
            <circle cx={cx} cy={cy} r={12} fill="#fff" stroke="#e5e7eb" strokeWidth={1} />
            <clipPath id={`avatar-clip-${payload.hour}`}>
                <circle cx={cx} cy={cy} r={10} />
            </clipPath>
            <image
                x={cx - 10}
                y={cy - 10}
                width={20}
                height={20}
                href={payload.user.avatar}
                clipPath={`url(#avatar-clip-${payload.hour})`}
                preserveAspectRatio="xMidYMid slice"
            />
        </g>
    );
}

// Helper function to get country flag URL
const getCountryFlagUrl = (code) => `https://purecatamphetamine.github.io/country-flag-icons/3x2/${code}.svg`;

function formatCount(n) {
    return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
}

function formatRevenue(n) {
    return n >= 1000 ? `$${(n / 1000).toFixed(1)}k` : `$${n}`;
}

function ChannelDonutTooltip({active, payload}) {
    if (!active || !payload?.length) return null;
    const data = payload[0];
    const p = data?.payload;
    if (!p) return null;
    return (
        <div className="dashboard-chart-tooltip dashboard-channel-tooltip">
            <div className="tooltip-date">{p.name}</div>
            <div className="tooltip-row">
                <span className="swatch visitors" />
                <span>Visitors: <strong>{(p.visitors >= 1000 ? `${(p.visitors / 1000).toFixed(1)}k` : p.visitors)}</strong></span>
            </div>
            <div className="tooltip-row">
                <span className="swatch revenue" />
                <span>Revenue: <strong>${(p.revenue >= 1000 ? `${(p.revenue / 1000).toFixed(1)}k` : p.revenue)}</strong></span>
            </div>
            {p.topSources?.length > 0 && (
                <>
                    <div className="tooltip-section">TOP SOURCES</div>
                    {p.topSources.map((s, i) => (
                        <div key={i} className="tooltip-row">
                            <span>{s.name}</span>
                            <strong>{s.pct}%</strong>
                        </div>
                    ))}
                </>
            )}
        </div>
    );
}

function ChartTooltip({active, payload, label}) {
    if (!active || !payload?.length) return null;
    const data = payload[0]?.payload;
    if (!data) return null;
    return (
        <div className="dashboard-chart-tooltip">
            <div className="tooltip-date">{data.fullDate}</div>
            <div className="tooltip-row">
                <span className="swatch visitors" />
                <span>Visitors: <strong>{data.visitors?.toLocaleString()}</strong></span>
            </div>
            <div className="tooltip-section">Revenue</div>
            <div className="tooltip-row">
                <span className="swatch revenue" />
                <span>New: <strong>${data.revenue?.toLocaleString()}</strong></span>
            </div>
            <div className="tooltip-row">
                <span>Revenue/visitor: <strong>${data.revenuePerVisitor}</strong></span>
            </div>
            <div className="tooltip-row">
                <span>Conversion rate: <strong>{data.conversionRate}%</strong></span>
            </div>
            {data.users?.length > 0 && (
                <>
                    <div className="tooltip-section">Users this day</div>
                    <div className="tooltip-users">
                        {data.users.map((u, i) => (
                            <span key={i} className="tooltip-user-avatar" title={u.name}>{u.initial}</span>
                        ))}
                    </div>
                </>
            )}
            {data.note && (
                <div className="tooltip-note">{data.note}</div>
            )}
            {data.note2 && (
                <div className="tooltip-note">• {data.note2}</div>
            )}
        </div>
    );
}

function ChartDot(props) {
    const {cx, cy, payload} = props;
    if (cx == null || cy == null) return null;
    const users = payload?.users || [];
    const first = users[0];
    if (!first) return <circle cx={cx} cy={cy} r={8} fill="#14B8FF" stroke="#fff" strokeWidth={2} className="dashboard-chart-dot" />;
    return (
        <g className="dashboard-chart-dot">
            <circle cx={cx} cy={cy} r={10} fill="#14B8FF" stroke="#fff" strokeWidth={2} />
            <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central" fill="#fff" fontSize={9} fontWeight="600">{first.initial}</text>
        </g>
    );
}

function KpiCard({value, change, isUp, isGoodDown, label, tooltip, checkColor, hasCheckbox, isOnlineCard}) {
    return (
        <div className="dashboard-kpi-card-wrapper">
            <div className="dashboard-kpi-card">
                {label != null && (
                    <div className="dashboard-kpi-card-title">
                        {hasCheckbox && (
                            <input
                                type="checkbox"
                                className={`kpi-checkbox ${checkColor || ''}`}
                                defaultChecked
                                readOnly
                            />
                        )}
                        <span className="kpi-label-text">{typeof label === 'object' ? label : label}</span>
                        {isOnlineCard && (
                            <span className="kpi-online-indicator">
                                <span className="kpi-online-ping" />
                                <span className="kpi-online-dot" />
                            </span>
                        )}
                    </div>
                )}
                <div className="dashboard-kpi-value-wrap">
                    <div className="dashboard-kpi-value">{value}</div>
                    {tooltip && (
                        <div className="dashboard-kpi-card-tooltip" role="tooltip">
                            {typeof tooltip === 'string' ? tooltip : tooltip}
                        </div>
                    )}
                    {change != null && (
                        <div className={`dashboard-kpi-change ${isUp ? 'up' : (isGoodDown ? 'good-down' : 'down')}`}>
                            <span className="kpi-change-value">{change}</span>
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className={`kpi-arrow-icon ${isUp ? 'rotate-up' : ''}`}>
                                <path fillRule="evenodd" d="M8 2a.75.75 0 0 1 .75.75v8.69l3.22-3.22a.75.75 0 1 1 1.06 1.06l-4.5 4.5a.75.75 0 0 1-1.06 0l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.22 3.22V2.75A.75.75 0 0 1 8 2Z" clipRule="evenodd" />
                            </svg>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function GeoDetailPopoverContent({ item }) {
    if (!item) return null;
    const revPerVisitor = item.revenuePerVisitor != null ? item.revenuePerVisitor : (item.visitors > 0 ? item.revenue / item.visitors : 0);
    const conversionRate = item.conversionRate != null ? item.conversionRate : 0;
    return (
        <>
            <div className="dashboard-tech-popover-row">
                <span className="dashboard-tech-popover-swatch visitors" />
                <span>Visitors</span>
                <strong>{item.visitors}</strong>
            </div>
            <div className="dashboard-tech-popover-row">
                <span className="dashboard-tech-popover-swatch revenue" />
                <span>Revenue</span>
                <strong>${item.revenue}</strong>
            </div>
            <div className="dashboard-tech-popover-row">
                <span>Revenue/visitor</span>
                <strong>${typeof revPerVisitor === 'number' ? revPerVisitor.toFixed(2) : revPerVisitor}</strong>
            </div>
            <div className="dashboard-tech-popover-row">
                <span>Conversion rate</span>
                <strong>{conversionRate}%</strong>
            </div>
        </>
    );
}

function GeoChoroplethMap({ geoMetric, countries, showCountryTooltip = true, isDark = false }) {
    const [hoveredCountry, setHoveredCountry] = useState(null);
    const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
    const mapWrapRef = useRef(null);

    const { valueByCountry, maxValue } = useMemo(() => {
        const map = {};
        let max = 0;
        countries.forEach(c => {
            const val = geoMetric === 'Revenue' ? c.revenue : c.visitors;
            map[c.name] = val;
            if (val > max) max = val;
        });
        return { valueByCountry: map, maxValue: max || 1 };
    }, [countries, geoMetric]);

    const getFill = (geoName) => {
        const ourName = GEO_NAME_TO_OUR[geoName] || geoName;
        const val = valueByCountry[ourName];
        if (val == null) return isDark ? '#1e293b' : '#FFFFFF';
        const pct = maxValue ? val / maxValue : 0;
        if (pct <= 0) return '#E0F2FE';
        if (pct >= 1) return '#0EA5E9';
        const blues = ['#E0F2FE', '#BAE6FD', '#7DD3FC', '#38BDF8', '#0EA5E9'];
        const i = pct * (blues.length - 1);
        const idx = Math.min(Math.floor(i), blues.length - 2);
        const t = i - idx;
        const hex = (s) => parseInt(s, 16);
        const lerp = (a, b) => Math.round(a + (b - a) * t);
        const s0 = blues[idx], s1 = blues[idx + 1];
        const r = lerp(hex(s0.slice(1, 3)), hex(s1.slice(1, 3)));
        const g = lerp(hex(s0.slice(3, 5)), hex(s1.slice(3, 5)));
        const b = lerp(hex(s0.slice(5, 7)), hex(s1.slice(5, 7)));
        return `rgb(${r},${g},${b})`;
    };

    const handleMapMouseMove = (evt) => {
        if (!showCountryTooltip) return;
        const e = evt.nativeEvent || evt;
        const x = e.clientX;
        const y = e.clientY;
        setTooltipPos({ x: x + 12, y: y + 12 });
        const el = document.elementFromPoint(x, y);
        if (!el) {
            setHoveredCountry(null);
            return;
        }
        let node = el;
        while (node && node !== document.body) {
            const name = node.getAttribute && node.getAttribute('data-country-name');
            if (name) {
                const country = countries.find((c) => c.name === name);
                setHoveredCountry(country || null);
                return;
            }
            node = node.parentNode;
        }
        setHoveredCountry(null);
    };

    const handleMapMouseLeave = () => {
        setHoveredCountry(null);
    };

    return (
        <div
            ref={mapWrapRef}
            className="dashboard-geo-map-wrap dashboard-geo-map-choropleth-wrap"
            onMouseMove={handleMapMouseMove}
            onMouseLeave={handleMapMouseLeave}
        >
            <ComposableMap
                projection="geoMercator"
                projectionConfig={{ scale: 100, center: [0, 20] }}
                width={800}
                height={400}
                style={{ width: '100%', height: 'auto' }}
            >
                <Geographies geography={GEO_MAP_URL}>
                    {({ geographies }) =>
                        geographies.map((geo) => {
                            const name = geo.properties?.name ?? geo.name ?? '';
                            const ourName = GEO_NAME_TO_OUR[name] || name;
                            const country = countries.find((c) => c.name === ourName);
                            return (
                                <g
                                    key={geo.rsmKey}
                                    data-country-name={ourName}
                                    style={{ cursor: country ? 'pointer' : 'default' }}
                                >
                                    <Geography
                                        geography={geo}
                                        fill={getFill(name)}
                                        stroke={isDark ? '#475569' : '#9CA3AF'}
                                        strokeWidth={0.6}
                                        style={{
                                            default: { outline: 'none' },
                                            hover: { outline: 'none', filter: 'brightness(0.95)' },
                                            pressed: { outline: 'none' }
                                        }}
                                    />
                                </g>
                            );
                        })
                    }
                </Geographies>
            </ComposableMap>
            {showCountryTooltip && hoveredCountry && (
                <div
                    className="dashboard-geo-map-tooltip dashboard-geo-map-tooltip-simple"
                    role="tooltip"
                    style={{ left: tooltipPos.x, top: tooltipPos.y }}
                >
                    <div className="dashboard-geo-map-tooltip-title">{hoveredCountry.name}</div>
                    <div className="dashboard-geo-map-tooltip-row">
                        <span className="dashboard-tech-popover-swatch visitors" />
                        <span>Visitors</span>
                        <strong>{hoveredCountry.visitors}</strong>
                    </div>
                </div>
            )}
        </div>
    );
}

function DetailsExpandIcon() {
    return (
        <svg className="dashboard-details-btn-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
        </svg>
    );
}

const DATE_RANGE_OPTIONS = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'last-24', label: 'Last 24 hours' },
    { value: 'last-7', label: 'Last 7 days' },
    { value: 'last-30', label: 'Last 30 days' },
    { value: 'week-to-date', label: 'Week to date' },
    { value: 'month-to-date', label: 'Month to date' }
];
const GRANULARITY_OPTIONS = [
    { value: 'hourly', label: 'Hourly' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' }
];

export default function Dashboard() {
    const { tokens, user } = useAuth();
    const accessToken = tokens?.accessToken ?? null;

    // ── Theme: dark / light mode ──
    const [dashTheme, setDashTheme] = useState(() => {
        try { return localStorage.getItem('oi_dash_theme') || 'light'; } catch { return 'light'; }
    });
    const [globeTheme, setGlobeTheme] = useState(() => {
        try { return localStorage.getItem('oi_globe_theme') || 'dark'; } catch { return 'dark'; }
    });
    const toggleDashTheme = useCallback(() => {
        setDashTheme(prev => {
            const next = prev === 'light' ? 'dark' : 'light';
            try { localStorage.setItem('oi_dash_theme', next); } catch {}
            return next;
        });
    }, []);
    const toggleGlobeTheme = useCallback(() => {
        setGlobeTheme(prev => {
            const next = prev === 'light' ? 'dark' : 'light';
            try { localStorage.setItem('oi_globe_theme', next); } catch {}
            return next;
        });
    }, []);

    // Chart colors based on dashboard theme
    const chartGridColor = dashTheme === 'dark' ? '#334155' : '#E5E7EB';
    const chartTickColor = dashTheme === 'dark' ? '#64748B' : '#9CA3AF';
    const chartAxisColor = dashTheme === 'dark' ? '#334155' : '#E5E7EB';

    const [selectedDate, setSelectedDate] = useState(new Date()); // End date for navigation
    const [rangeStartDate, setRangeStartDate] = useState(null); // Start date for range mode
    const [dateMode, setDateMode] = useState('single'); // 'single' for day navigation, 'range' for date ranges
    const [dateRange, setDateRange] = useState('today'); // Current preset selection
    const [rangeOffset, setRangeOffset] = useState(0); // How many periods back from today
    const [granularity, setGranularity] = useState('hourly');
    const [dateDropdownOpen, setDateDropdownOpen] = useState(false);
    const [granularityDropdownOpen, setGranularityDropdownOpen] = useState(false);
    const [referrerTab, setReferrerTab] = useState('Channel');
    const [geoTab, setGeoTab] = useState('Country');
    const [geoMetric, setGeoMetric] = useState('Visitors'); // 'Visitors' | 'Revenue'
    const [geoDetailModalOpen, setGeoDetailModalOpen] = useState(false);
    const [geoDetailSearch, setGeoDetailSearch] = useState('');
    const [hostTab, setHostTab] = useState('Hostname');
    const [hostMetric, setHostMetric] = useState('Visitors');
    const [hostDetailModalOpen, setHostDetailModalOpen] = useState(false);
    const [hostDetailSearch, setHostDetailSearch] = useState('');
    const [techTab, setTechTab] = useState('Browser');
    const [techMetric, setTechMetric] = useState('Visitors'); // 'Visitors' | 'Revenue'
    const [techDetailModalOpen, setTechDetailModalOpen] = useState(false);
    const [techDetailSearch, setTechDetailSearch] = useState('');
    const [journeyTab, setJourneyTab] = useState('Goal');
    const [goalDetailModalOpen, setGoalDetailModalOpen] = useState(false);
    const [goalDetailSearch, setGoalDetailSearch] = useState('');
    const [userSearch, setUserSearch] = useState('');

    // Globe Modal State
    const [globeModalOpen, setGlobeModalOpen] = useState(false);
    const [globeLoading, setGlobeLoading] = useState(true);
    const [isAutoPanning, setIsAutoPanning] = useState(true);
    const [globeViewState, setGlobeViewState] = useState({
        longitude: 0,
        latitude: 20,
        zoom: 1.5,
        pitch: 0,
        bearing: 0
    });
    const globeMapRef = useRef(null);
    const spaceParallaxRef = useRef(null); // stores rAF cleanup fn

    // Auth user geolocation for globe pin
    const [myLocation, setMyLocation] = useState(null); // { lat, lng }
    useEffect(() => {
        if (!globeModalOpen) return;
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (pos) => setMyLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
                () => setMyLocation(null),
                { enableHighAccuracy: false, timeout: 8000 }
            );
        }
    }, [globeModalOpen]);

    // Cleanup space parallax when globe modal closes
    useEffect(() => {
        if (!globeModalOpen && spaceParallaxRef.current) {
            spaceParallaxRef.current();
            spaceParallaxRef.current = null;
        }
    }, [globeModalOpen]);

    // Auto-refresh realtime data every 5s when globe is open
    useEffect(() => {
        if (!globeModalOpen || !accessToken) return;
        const fetchRealtime = async () => {
            try {
                const [rtV, rtA] = await Promise.all([
                    fetchRealtimeVisitors({ accessToken }),
                    fetchRealtimeActivity({ accessToken }),
                ]);
                if (rtV.ok) setApiRealtimeVisitors(rtV.data ?? []);
                else console.warn('[Dashboard] fetchRealtimeVisitors failed:', rtV.error);
                if (rtA.ok) setApiRealtimeActivity(rtA.data ?? []);
                else console.warn('[Dashboard] fetchRealtimeActivity failed:', rtA.error);
            } catch (err) {
                console.warn('[Dashboard] Globe realtime fetch error:', err);
            }
        };
        fetchRealtime(); // Fetch immediately on globe open
        const interval = setInterval(fetchRealtime, 5_000);
        return () => clearInterval(interval);
    }, [globeModalOpen, accessToken]);
    const [journeySearch, setJourneySearch] = useState('');
    const [channelFilter, setChannelFilter] = useState(null);
    const [campaignFilter, setCampaignFilter] = useState(null);
    const [channelDropdownOpen, setChannelDropdownOpen] = useState(false);
    const [campaignDropdownOpen, setCampaignDropdownOpen] = useState(false);

    // Active filters state
    const [activeFilters, setActiveFilters] = useState([]);

    // Add filter function
    const addFilter = (type, value, code = null) => {
        setActiveFilters(prev => {
            // Check if filter already exists
            const exists = prev.some(f => f.type === type && f.value === value);
            if (exists) return prev;
            return [...prev, { type, value, code }];
        });
    };

    // Remove filter function
    const removeFilter = (type, value) => {
        setActiveFilters(prev => prev.filter(f => !(f.type === type && f.value === value)));
    };

    // Clear all filters
    const clearAllFilters = () => {
        setActiveFilters([]);
    };
    const [acquisitionMetric, setAcquisitionMetric] = useState('Visitors'); // 'Visitors' | 'Revenue'
    const [channelDetailModalOpen, setChannelDetailModalOpen] = useState(false);
    const [channelDetailSearch, setChannelDetailSearch] = useState('');
    const [referrerDetailModalOpen, setReferrerDetailModalOpen] = useState(false);
    const [referrerDetailSearch, setReferrerDetailSearch] = useState('');
    const [campaignDetailModalOpen, setCampaignDetailModalOpen] = useState(false);
    const [campaignDetailSearch, setCampaignDetailSearch] = useState('');
    const [keywordDetailModalOpen, setKeywordDetailModalOpen] = useState(false);
    const [keywordDetailSearch, setKeywordDetailSearch] = useState('');
    const [tooltipData, setTooltipData] = useState(null);

    // API data state - null means use mock; set when API returns
    const [apiKpis, setApiKpis] = useState(null);
    const [apiChartData, setApiChartData] = useState(null);
    const [apiRealtimeVisitors, setApiRealtimeVisitors] = useState(null);
    const [apiRealtimeActivity, setApiRealtimeActivity] = useState(null);
    const [apiChannels, setApiChannels] = useState(null);
    const [apiReferrals, setApiReferrals] = useState(null);
    const [apiCampaigns, setApiCampaigns] = useState(null);
    const [apiKeywords, setApiKeywords] = useState(null);
    const [apiCountries, setApiCountries] = useState(null);
    const [apiRegions, setApiRegions] = useState(null);
    const [apiCities, setApiCities] = useState(null);
    const [apiBrowsers, setApiBrowsers] = useState(null);
    const [apiOs, setApiOs] = useState(null);
    const [apiDevices, setApiDevices] = useState(null);
    const [apiHostnames, setApiHostnames] = useState(null);
    const [apiPages, setApiPages] = useState(null);
    const [apiEntryPages, setApiEntryPages] = useState(null);
    const [apiExitLinks, setApiExitLinks] = useState(null);
    const [apiJourney, setApiJourney] = useState(null);
    const [apiVisitors, setApiVisitors] = useState(null);
    const [dashboardLoading, setDashboardLoading] = useState(true);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
    const channelDropdownRef = useRef(null);
    const campaignDropdownRef = useRef(null);
    const dateDropdownRef = useRef(null);
    const granularityDropdownRef = useRef(null);
    const [currentTime, setCurrentTime] = useState('');

    // Handle mouse move for tooltip positioning
    const handleRowMouseMove = (e, data) => {
        const rowRect = e.currentTarget.getBoundingClientRect();
        const mouseX = e.clientX;
        const mouseY = e.clientY;

        // Find parent card boundary
        const parentCard = e.currentTarget.closest('.dashboard-section-card, .custom-card, .dashboard-chart-card');
        const cardRect = parentCard ? parentCard.getBoundingClientRect() : rowRect;

        // Calculate mouse position relative to parent card
        const relativeX = mouseX - cardRect.left;
        const relativeY = mouseY - cardRect.top;
        const cardWidth = cardRect.width;
        const cardHeight = cardRect.height;

        // Decide tooltip side based on mouse position within parent card (70% threshold)
        const tooltipSide = relativeX > cardWidth * 0.7 ? 'left' : 'right';

        // Decide vertical position based on mouse position within parent card
        const tooltipVertical = relativeY > cardHeight * 0.7 ? 'top' : 'bottom';

        setTooltipPos({
            x: mouseX - rowRect.left,
            y: mouseY - rowRect.top,
            side: tooltipSide,
            vertical: tooltipVertical
        });
        setTooltipData(data);
    };

    const handleRowMouseLeave = () => {
        setTooltipData(null);
    };
    useEffect(() => {
        const t = new Date();
        setCurrentTime(t.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase());
    }, []);

    const handleChannelFilter = (value) => {
        setChannelFilter(value);
        setChannelDropdownOpen(false);
    };

    const handleCampaignFilter = (value) => {
        setCampaignFilter(value);
        setCampaignDropdownOpen(false);
    };

    // Derived data: API only, empty when loading or no data
    const channelsData = apiChannels ?? [];
    const campaignsData = apiCampaigns ?? [];
    const referralsData = apiReferrals ?? [];
    const keywordsData = apiKeywords ?? [];
    const countriesData = apiCountries ?? [];
    const regionsData = apiRegions ?? [];
    const citiesData = apiCities ?? [];
    const browsersData = apiBrowsers ?? [];
    const osListData = apiOs ?? [];
    const devicesData = apiDevices ?? [];
    const hostnamesData = apiHostnames ?? [];
    const pagesData = apiPages ?? [];
    const entryPagesData = apiEntryPages ?? [];
    const exitLinksData = apiExitLinks ?? [];
    const realtimeVisitorsData = apiRealtimeVisitors ?? [];
    const realtimeActivityData = apiRealtimeActivity ?? [];
    const journeyData = apiJourney ?? { goalsTotalVisitors: 0, goals: [], funnelStages: [], journeyGoal: [], journeyPayment: [] };
    const visitorLatestActivity = useMemo(() => {
        const m = {};
        (realtimeActivityData || []).forEach((a) => {
            m[a.visitorName] = { page: a.page, time: a.time, action: a.action, type: a.type };
        });
        return m;
    }, [realtimeActivityData]);
    const visitorsData = apiVisitors ?? [];

    const filteredChannels = !channelFilter || channelFilter === 'All'
        ? channelsData
        : channelsData.filter(c => c.name === channelFilter);

    const filteredCampaigns = !campaignFilter || campaignFilter === 'All'
        ? campaignsData
        : campaignsData.filter(c => c.utm === campaignFilter);

    const maxReferral = referralsData.length ? Math.max(...referralsData.map(r => r.visitors ?? 0)) : 0;
    const maxReferralRevenue = referralsData.length ? Math.max(...referralsData.map(r => r.revenue ?? 0)) : 0;
    const maxCountry = countriesData.length ? Math.max(...countriesData.map(c => c.visitors ?? 0)) : 0;
    const maxCountryRevenue = countriesData.length ? Math.max(...countriesData.map(c => c.revenue ?? 0)) : 0;
    const maxRegion = regionsData.length ? Math.max(...regionsData.map(r => r.visitors ?? 0)) : 0;
    const maxRegionRevenue = regionsData.length ? Math.max(...regionsData.map(r => r.revenue ?? 0)) : 0;
    const maxCity = citiesData.length ? Math.max(...citiesData.map(c => c.visitors ?? 0)) : 0;
    const maxCityRevenue = citiesData.length ? Math.max(...citiesData.map(c => c.revenue ?? 0)) : 0;
    const maxBrowser = browsersData.length ? Math.max(...browsersData.map(b => b.visitors ?? 0)) : 0;
    const maxBrowserRevenue = browsersData.length ? Math.max(...browsersData.map(b => b.revenue ?? 0), 1) : 1;
    const maxOS = osListData.length ? Math.max(...osListData.map(o => o.visitors ?? 0)) : 0;
    const maxOSRevenue = osListData.length ? Math.max(...osListData.map(o => o.revenue ?? 0), 1) : 1;
    const maxDevice = devicesData.length ? Math.max(...devicesData.map(d => d.visitors ?? 0)) : 0;
    const maxDeviceRevenue = devicesData.length ? Math.max(...devicesData.map(d => d.revenue ?? 0), 1) : 1;
    const maxHostname = hostnamesData.length ? Math.max(...hostnamesData.map(h => h.visitors ?? 0)) : 0;
    const maxHostnameRevenue = hostnamesData.length ? Math.max(...hostnamesData.map(h => h.revenue ?? 0), 1) : 1;
    const maxPage = pagesData.length ? Math.max(...pagesData.map(p => p.visitors ?? 0)) : 0;
    const maxPageRevenue = pagesData.length ? Math.max(...pagesData.map(p => p.revenue ?? 0), 1) : 1;
    const maxEntryPage = entryPagesData.length ? Math.max(...entryPagesData.map(p => p.visitors ?? 0)) : 0;
    const maxEntryPageRevenue = entryPagesData.length ? Math.max(...entryPagesData.map(p => p.revenue ?? 0), 1) : 1;
    const maxExitLink = exitLinksData.length ? Math.max(...exitLinksData.map(e => e.visitors ?? 0)) : 0;
    const maxExitLinkRevenue = exitLinksData.length ? Math.max(...exitLinksData.map(e => e.revenue ?? 0), 1) : 1;
    const maxKeywordVisitors = keywordsData.length ? Math.max(...keywordsData.map(k => k.visitors ?? 0)) : 0;
    const maxKeywordRevenue = keywordsData.length ? Math.max(...keywordsData.map(k => k.revenueEst ?? 0)) : 0;

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (channelDropdownRef.current && !channelDropdownRef.current.contains(e.target)) {
                setChannelDropdownOpen(false);
            }
            if (campaignDropdownRef.current && !campaignDropdownRef.current.contains(e.target)) {
                setCampaignDropdownOpen(false);
            }
            if (dateDropdownRef.current && !dateDropdownRef.current.contains(e.target)) {
                setDateDropdownOpen(false);
            }
            if (granularityDropdownRef.current && !granularityDropdownRef.current.contains(e.target)) {
                setGranularityDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Helper to check if two dates are the same day
    const isSameDay = (d1, d2) => {
        return d1.getFullYear() === d2.getFullYear() &&
               d1.getMonth() === d2.getMonth() &&
               d1.getDate() === d2.getDate();
    };

    // Format a date as "Feb 3, 2026"
    const formatDate = (date) => {
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    // Get today's date (normalized to start of day for comparison)
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    // Get range days based on preset
    const getRangeDays = (preset) => {
        switch (preset) {
            case 'last-24': return 1;
            case 'last-7': return 7;
            case 'last-30': return 30;
            case 'week-to-date': return today.getDay() || 7; // Days since start of week
            case 'month-to-date': return today.getDate(); // Days since start of month
            default: return 1;
        }
    };

    // Calculate current date range based on mode and offset
    const calculateDateRange = () => {
        if (dateMode === 'single') {
            return { start: selectedDate, end: selectedDate };
        }

        const rangeDays = getRangeDays(dateRange);
        const endDate = new Date(today);
        endDate.setDate(endDate.getDate() - rangeOffset);
        const startDate = new Date(endDate);
        startDate.setDate(startDate.getDate() - rangeDays + 1);

        return { start: startDate, end: endDate };
    };

    const currentRange = calculateDateRange();

    // Check if we're at today (can't go forward)
    const isAtToday = dateMode === 'single'
        ? isSameDay(selectedDate, today)
        : rangeOffset === 0;

    // Check if selected date is yesterday (for single mode)
    const isYesterday = dateMode === 'single' && isSameDay(selectedDate, yesterday);
    const isTodaySingle = dateMode === 'single' && isSameDay(selectedDate, today);

    // Format date label based on mode
    const getDateLabel = () => {
        if (dateMode === 'single') {
            if (isTodaySingle) return 'Today';
            if (isYesterday) return 'Yesterday';
            return formatDate(selectedDate);
        }

        // Range mode - show date range if offset > 0, otherwise show preset label
        if (rangeOffset === 0) {
            return DATE_RANGE_OPTIONS.find(o => o.value === dateRange)?.label ?? dateRange;
        }

        // Show date range like "Feb 1, 2026 → Feb 2, 2026"
        return `${formatDate(currentRange.start)} → ${formatDate(currentRange.end)}`;
    };

    // Navigate to previous period
    const goToPrevious = () => {
        if (dateMode === 'single') {
            const newDate = new Date(selectedDate);
            newDate.setDate(newDate.getDate() - 1);
            setSelectedDate(newDate);
        } else {
            setRangeOffset(prev => prev + 1);
        }
    };

    // Navigate to next period
    const goToNext = () => {
        if (isAtToday) return;

        if (dateMode === 'single') {
            const newDate = new Date(selectedDate);
            newDate.setDate(newDate.getDate() + 1);
            setSelectedDate(newDate);
        } else {
            setRangeOffset(prev => Math.max(0, prev - 1));
        }
    };

    // Handle date range selection from dropdown
    const handleDateRangeSelect = (value) => {
        setDateRange(value);
        setRangeOffset(0); // Reset offset when selecting new preset

        // Auto-set appropriate granularity based on date range
        if (value === 'today' || value === 'yesterday' || value === 'last-24') {
            setGranularity('hourly');
        } else if ((value === 'last-7' || value === 'week-to-date') && granularity === 'weekly') {
            // Weekly not available for 7 days / week to date, switch to daily
            setGranularity('daily');
        } else if ((value === 'last-30' || value === 'month-to-date') && granularity === 'hourly') {
            // Hourly not available for 30 days+, switch to daily
            setGranularity('daily');
        }

        if (value === 'today') {
            setSelectedDate(new Date());
            setDateMode('single');
        } else if (value === 'yesterday') {
            const yd = new Date();
            yd.setDate(yd.getDate() - 1);
            setSelectedDate(yd);
            setDateMode('single');
        } else {
            setDateMode('range');
        }
        setDateDropdownOpen(false);
    };

    const dateRangeLabel = getDateLabel();

    // Filter granularity options based on date range
    // Today, Yesterday, Last 24 hours → Only Hourly
    // Last 7 days, Week to date → Hourly and Daily
    // Last 30 days, Month to date → Daily and Weekly
    const getAvailableGranularityOptions = () => {
        if (dateRange === 'today' || dateRange === 'yesterday' || dateRange === 'last-24') {
            return GRANULARITY_OPTIONS.filter(o => o.value === 'hourly');
        }
        if (dateRange === 'last-7' || dateRange === 'week-to-date') {
            return GRANULARITY_OPTIONS.filter(o => o.value === 'hourly' || o.value === 'daily');
        }
        if (dateRange === 'last-30' || dateRange === 'month-to-date') {
            return GRANULARITY_OPTIONS.filter(o => o.value === 'daily' || o.value === 'weekly');
        }
        return GRANULARITY_OPTIONS;
    };

    const availableGranularityOptions = getAvailableGranularityOptions();

    const granularityLabel = GRANULARITY_OPTIONS.find(o => o.value === granularity)?.label ?? granularity;

    // Format date for API (ISO YYYY-MM-DD)
    const toApiDate = (d) => d.toISOString().slice(0, 10);

    const opts = useMemo(() => ({ accessToken }), [accessToken]);
    const dateFrom = toApiDate(currentRange.start);
    const dateTo = toApiDate(currentRange.end);
    const chartGranularity = (dateRange === 'today' || dateRange === 'yesterday' || dateRange === 'last-24')
        ? 'hourly'
        : granularity === 'weekly' ? 'weekly' : 'daily';

    // Chunk 1: Only summary on load (KPIs + chart + acquisition channel) – one fast API call
    const fetchDashboardData = useCallback(async () => {
        if (!accessToken) return;
        setDashboardLoading(true);
        try {
            const summaryRes = await fetchSummary({
                date_from: dateFrom,
                date_to: dateTo,
                granularity: chartGranularity,
                accessToken,
            });
            if (summaryRes.ok && summaryRes.data) {
                if (summaryRes.data.kpis) setApiKpis(summaryRes.data.kpis);
                if (summaryRes.data.chart) setApiChartData(summaryRes.data.chart);
                if (summaryRes.data.acquisition) setApiChannels(normalizeChannels(summaryRes.data.acquisition));
            }
            // Chunk 2: Load realtime + journey + visitors in background (data appears in chunks)
            Promise.all([
                fetchRealtimeVisitors(opts),
                fetchRealtimeActivity(opts),
                fetchJourney(opts),
                fetchVisitors({ q: userSearch || undefined, accessToken }),
            ]).then(([rtV, rtA, j, v]) => {
                if (rtV.ok) setApiRealtimeVisitors(rtV.data ?? []);
                else console.warn('[Dashboard] Realtime visitors fetch failed:', rtV.error);
                if (rtA.ok) setApiRealtimeActivity(rtA.data ?? []);
                else console.warn('[Dashboard] Realtime activity fetch failed:', rtA.error);
                if (j.ok && j.data) setApiJourney(j.data);
                if (v.ok && v.data) setApiVisitors(v.data);
            }).catch((err) => {
                console.warn('[Dashboard] Background data fetch error:', err);
            });
        } catch (err) {
            console.warn('Dashboard API fetch error:', err);
        } finally {
            setDashboardLoading(false);
        }
    }, [accessToken, dateFrom, dateTo, chartGranularity, userSearch, refreshTrigger]);

    // Lazy-load Acquisition tab data only when user opens that sub-tab
    useEffect(() => {
        if (!accessToken) return;
        if (referrerTab === 'Referrer' && apiReferrals === null) {
            fetchAcquisition({ group_by: 'referrer', ...opts }).then((r) => { if (r.ok && r.data) setApiReferrals(normalizeReferrals(r.data)); });
        } else if (referrerTab === 'Campaign' && apiCampaigns === null) {
            fetchAcquisition({ group_by: 'campaign', ...opts }).then((r) => { if (r.ok && r.data) setApiCampaigns(r.data); });
        } else if (referrerTab === 'Keyword' && apiKeywords === null) {
            fetchAcquisition({ group_by: 'keyword', ...opts }).then((r) => { if (r.ok && r.data) setApiKeywords(r.data); });
        }
    }, [accessToken, referrerTab, apiReferrals, apiCampaigns, apiKeywords, opts]);

    // Lazy-load Geo tab data only when user opens that sub-tab
    useEffect(() => {
        if (!accessToken) return;
        if (geoTab === 'Country' && apiCountries === null) {
            fetchGeo({ group_by: 'country', ...opts }).then((r) => { if (r.ok && r.data) setApiCountries(normalizeGeo(r.data)); });
        } else if (geoTab === 'Region' && apiRegions === null) {
            fetchGeo({ group_by: 'region', ...opts }).then((r) => { if (r.ok && r.data) setApiRegions(normalizeGeo(r.data)); });
        } else if (geoTab === 'City' && apiCities === null) {
            fetchGeo({ group_by: 'city', ...opts }).then((r) => { if (r.ok && r.data) setApiCities(normalizeGeo(r.data)); });
        }
    }, [accessToken, geoTab, apiCountries, apiRegions, apiCities, opts]);

    // Lazy-load Tech tab data only when user opens that sub-tab
    useEffect(() => {
        if (!accessToken) return;
        if (techTab === 'Browser' && apiBrowsers === null) {
            fetchTech({ group_by: 'browser', ...opts }).then((r) => { if (r.ok && r.data) setApiBrowsers(r.data); });
        } else if (techTab === 'OS' && apiOs === null) {
            fetchTech({ group_by: 'os', ...opts }).then((r) => { if (r.ok && r.data) setApiOs(r.data); });
        } else if (techTab === 'Device' && apiDevices === null) {
            fetchTech({ group_by: 'device', ...opts }).then((r) => { if (r.ok && r.data) setApiDevices(r.data); });
        }
    }, [accessToken, techTab, apiBrowsers, apiOs, apiDevices, opts]);

    // Lazy-load Host tab data only when user opens that sub-tab
    useEffect(() => {
        if (!accessToken) return;
        if (hostTab === 'Hostname' && apiHostnames === null) {
            fetchHost({ group_by: 'hostname', ...opts }).then((r) => { if (r.ok && r.data) setApiHostnames(r.data); });
        } else if (hostTab === 'Page' && apiPages === null) {
            fetchHost({ group_by: 'page', ...opts }).then((r) => { if (r.ok && r.data) setApiPages(r.data); });
        } else if (hostTab === 'Entry page' && apiEntryPages === null) {
            fetchHost({ group_by: 'entry_page', ...opts }).then((r) => { if (r.ok && r.data) setApiEntryPages(r.data); });
        } else if (hostTab === 'Exit link' && apiExitLinks === null) {
            fetchHost({ group_by: 'exit_link', ...opts }).then((r) => { if (r.ok && r.data) setApiExitLinks(r.data); });
        }
    }, [accessToken, hostTab, apiHostnames, apiPages, apiEntryPages, apiExitLinks, opts]);

    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'r') {
                e.preventDefault();
                setRefreshTrigger((t) => t + 1);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    // Normalize API shapes to match UI expectations
    function normalizeChannels(arr) {
        const colors = ['#93C5FD', '#7DD3FC', '#A5B4FC', '#C4B5FD', '#DDD6FE', '#E0E7FF', '#E5E7EB'];
        return (arr || []).map((c, i) => ({ ...c, color: c.color || colors[i % colors.length], topSources: c.topSources || [] }));
    }
    function normalizeReferrals(arr) {
        return (arr || []).map((r) => ({
            ...r,
            icon: r.icon ?? '🔗',
            revenue: r.revenue ?? 0,
            revenuePerVisitor: r.revenuePerVisitor ?? 0,
            conversionRate: r.conversionRate ?? 0,
        }));
    }
    function normalizeGeo(arr) {
        return (arr || []).map((g) => ({ ...g, code: g.code || g.countryCode, revenuePerVisitor: g.revenuePerVisitor ?? 0, conversionRate: g.conversionRate ?? 0 }));
    }

    const TOTAL_CHANNEL_VISITORS = channelsData.reduce((s, c) => s + (c.visitors ?? 0), 0);
    const TOTAL_CAMPAIGN_VISITORS = campaignsData.reduce((s, c) => s + (c.visitors ?? 0), 0);
    const TOTAL_CHANNEL_REVENUE = channelsData.reduce((s, c) => s + (c.revenue ?? 0), 0);
    const TOTAL_CAMPAIGN_REVENUE = campaignsData.reduce((s, c) => s + (c.revenue ?? 0), 0);

    // Chart data comes pre-filled from the API (all 24 hours for hourly, all dates for daily)
    const chartDataForDisplay = apiChartData && Array.isArray(apiChartData) && apiChartData.length > 0
        ? apiChartData
        : [];

    const kpis = apiKpis ?? {
        visitors: 0,
        revenue: 0,
        conversionRate: '-',
        revenuePerVisitor: '$0',
        bounceRate: '-',
        sessionTime: '-',
        onlineNow: 0,
    };
    const kpiVisitors = typeof kpis.visitors === 'number' ? String(kpis.visitors) : (kpis.visitors ?? '390');
    const kpiRevenue = typeof kpis.revenue === 'number' ? (kpis.revenue >= 1000 ? `$${(kpis.revenue / 1000).toFixed(1)}k` : `$${kpis.revenue}`) : (kpis.revenue ?? '$0');
    const kpiConversionRate = kpis.conversionRate ?? '-';
    const kpiRevenuePerVisitor = kpis.revenuePerVisitor ?? '$0';
    const kpiBounceRate = kpis.bounceRate ?? '84%';
    const kpiSessionTime = kpis.sessionTime ?? '1m 45s';
    const kpiOnlineNow = typeof kpis.onlineNow === 'number' ? String(kpis.onlineNow) : (kpis.onlineNow ?? '11');

    if (!accessToken) {
        return (
            <div style={{
                maxWidth: '1400px',
                margin: '0 auto',
                padding: '24px',
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
            }}>
                {/* Breadcrumb */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    color: '#666',
                    marginBottom: '16px'
                }}>
                    <span>Analytics Dashboard</span>
                </div>

                <div className={`dashboard-page${dashTheme === 'dark' ? ' dashboard-dark' : ''}`} style={{marginLeft: '-24px', marginRight: '-24px'}}>
                    <main className="dashboard-main" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', flexDirection: 'column', gap: '1rem' }}>
                        <p className="text-base opacity-90">Please log in to view your dashboard.</p>
                    </main>
                </div>
            </div>
        );
    }

    return (
        <div style={{
            maxWidth: '1400px',
            margin: '0 auto',
            padding: '24px',
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
        }}>
            {/* Breadcrumb */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                color: '#666',
                marginBottom: '16px'
            }}>
                <span>Analytics Dashboard</span>
            </div>

            <div className={`dashboard-page${dashTheme === 'dark' ? ' dashboard-dark' : ''}`} style={{marginLeft: '-24px', marginRight: '-24px'}}>
            <main className="dashboard-main">
                <section className="dashboard-topbar">
                    <div className="dashboard-topbar-inner flex-w-full">
                        <div className="dashboard-topbar-left flex-w-full flex-col sm-flex-row">
                            <div className="dashboard-logo-join-item single-item-join-divider btn-sm">
                            <img src="/images/optimus-icon.png" alt="Optmus" className="!size-5 rounded" />
                                <span className="dashboard-logo-title text-base">Optimus</span>
                            </div>
                            <div className="dashboard-join-group join-divider">
                                <button
                                    type="button"
                                    className="dashboard-join-btn btn-square btn-ghost join-item btn-sm"
                                    aria-label="Previous period"
                                    onClick={goToPrevious}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="m15 18-6-6 6-6" /></svg>
                                </button>
                                <div className="dashboard-join-item-wrap join-item-wrap h-8" ref={dateDropdownRef}>
                                    <button
                                        type="button"
                                        className="dashboard-join-btn join-item btn-sm dashboard-join-btn-center"
                                        onClick={() => setDateDropdownOpen(o => !o)}
                                        aria-expanded={dateDropdownOpen}
                                        aria-haspopup="listbox"
                                    >
                                        <span className="dashboard-join-btn-label text-base">{dateRangeLabel}</span>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide-chevron-down opacity-30" aria-hidden><path d="m6 9 6 6 6-6" /></svg>
                                    </button>
                                    {dateDropdownOpen && (
                                        <div className="dashboard-filter-menu dashboard-date-menu" role="listbox">
                                            {currentTime && (
                                                <div className="dashboard-filter-menu-time">Current time: {currentTime}</div>
                                            )}
                                            {DATE_RANGE_OPTIONS.map(opt => (
                                                <button
                                                    key={opt.value}
                                                    type="button"
                                                    role="option"
                                                    aria-selected={dateRange === opt.value && rangeOffset === 0}
                                                    className={`dashboard-filter-menu-item ${dateRange === opt.value && rangeOffset === 0 ? 'active' : ''}`}
                                                    onClick={() => handleDateRangeSelect(opt.value)}
                                                >
                                                    {opt.label}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    className={`dashboard-join-btn btn-square btn-ghost join-item btn-sm ${isAtToday ? 'dashboard-join-btn-disabled cursor-not-allowed' : ''}`}
                                    aria-label="Next period"
                                    disabled={isAtToday}
                                    onClick={goToNext}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`lucide-chevron-right ${isAtToday ? 'opacity-30' : ''}`} aria-hidden><path d="m9 18 6-6-6-6" /></svg>
                                </button>
                            </div>
                            <div className="dashboard-granularity-wrap hidden sm-block" ref={granularityDropdownRef}>
                                <button
                                    type="button"
                                    className="dashboard-logo-join-item single-item-join-divider btn-sm"
                                    onClick={() => setGranularityDropdownOpen(o => !o)}
                                    aria-expanded={granularityDropdownOpen}
                                    aria-haspopup="listbox"
                                    aria-label="Switch granularity"
                                >
                                    <span className="dashboard-join-btn-label text-base">{granularityLabel}</span>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide-chevron-down opacity-30" aria-hidden><path d="m6 9 6 6 6-6" /></svg>
                                </button>
                                {granularityDropdownOpen && (
                                    <div className="dashboard-filter-menu" role="listbox">
                                        {availableGranularityOptions.map(opt => (
                                            <button
                                                key={opt.value}
                                                type="button"
                                                role="option"
                                                aria-selected={granularity === opt.value}
                                                className={`dashboard-filter-menu-item ${granularity === opt.value ? 'active' : ''}`}
                                                onClick={() => {
                                                    setGranularity(opt.value);
                                                    setGranularityDropdownOpen(false);
                                                }}
                                            >
                                                {opt.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <button type="button" className="dashboard-logo-join-item dashboard-refresh-btn btn-square btn-sm" aria-label="Refresh analytics data" title="Refresh analytics data (Cmd+R / Ctrl+R)" onClick={() => setRefreshTrigger((t) => t + 1)} disabled={dashboardLoading}>
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="dashboard-refresh-icon" aria-hidden>
                                    <path fillRule="evenodd" d="M13.836 2.477a.75.75 0 0 1 .75.75v3.182a.75.75 0 0 1-.75.75h-3.182a.75.75 0 0 1 0-1.5h1.37l-.84-.841a4.5 4.5 0 0 0-7.08.932.75.75 0 0 1-1.3-.75 6 6 0 0 1 9.44-1.242l.842.84V3.227a.75.75 0 0 1 .75-.75Zm-.911 7.5A.75.75 0 0 1 13.199 10a6 6 0 0 1-9.44 1.241l-.84-.84v1.371a.75.75 0 0 1-1.5 0V8.591a.75.75 0 0 1 .75-.75H5.35a.75.75 0 0 1 0 1.5H3.98l.841.841a4.5 4.5 0 0 0 7.08-.932.75.75 0 0 1 1.025-.273Z" clipRule="evenodd" />
                                </svg>
                            </button>
                            <button
                                type="button"
                                className="dashboard-logo-join-item btn-square btn-sm"
                                aria-label={dashTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                                title={dashTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                                onClick={toggleDashTheme}
                            >
                                {dashTheme === 'dark' ? (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="4"/><path d="M12 2v2"/><path d="M12 20v2"/><path d="m4.93 4.93 1.41 1.41"/><path d="m17.66 17.66 1.41 1.41"/><path d="M2 12h2"/><path d="M20 12h2"/><path d="m6.34 17.66-1.41 1.41"/><path d="m19.07 4.93-1.41 1.41"/>
                                    </svg>
                                ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/>
                                    </svg>
                                )}
                            </button>
                        </div>
                    </div>
                </section>

                {/* Active Filters */}
                {activeFilters.length > 0 && (
                    <section className="dashboard-active-filters">
                        {activeFilters.map((filter, idx) => (
                            <div key={idx} className="dashboard-filter-chip">
                                {filter.code && (
                                    <img
                                        src={getCountryFlagUrl(filter.code)}
                                        alt=""
                                        className="dashboard-filter-chip-flag"
                                    />
                                )}
                                <span className="dashboard-filter-chip-label">
                                    {filter.type} is <strong>{filter.value}</strong>
                                </span>
                                <button
                                    type="button"
                                    className="dashboard-filter-chip-remove"
                                    onClick={() => removeFilter(filter.type, filter.value)}
                                    aria-label={`Remove ${filter.type} filter`}
                                >
                                    ×
                                </button>
                            </div>
                        ))}
                        {activeFilters.length > 1 && (
                            <button
                                type="button"
                                className="dashboard-filter-clear-all"
                                onClick={clearAllFilters}
                            >
                                Clear all
                            </button>
                        )}
                    </section>
                )}

                <section className="dashboard-chart-card custom-card">
                    {dashboardLoading ? (
                        <div className="dashboard-loading" style={{ minHeight: 320, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--dash-muted, #64748B)' }}>
                            Loading…
                        </div>
                    ) : (
                        <>
                    <div className="dashboard-kpi-grid">
                        <KpiCard value={kpiVisitors} label="Visitors" hasCheckbox checkColor="blue" tooltip={`${kpiVisitors} unique visitors`} />
                        <KpiCard value={kpiRevenue} label="Revenue" hasCheckbox checkColor="coral" tooltip={`${kpiRevenue} revenue`} />
                        <KpiCard value={kpiConversionRate} label="Conversion rate" tooltip="Conversion rate" />
                        <KpiCard value={kpiRevenuePerVisitor} label="Revenue/visitor" tooltip="Revenue per visitor" />
                        <KpiCard value={kpiBounceRate} label="Bounce rate" tooltip={`${kpiBounceRate} bounce rate`} />
                        <KpiCard value={kpiSessionTime} label="Session time" tooltip={`${kpiSessionTime} avg session time`} />
                        <KpiCard value={kpiOnlineNow} label="Online" isOnlineCard tooltip="Watch in realtime" />
                    </div>
                    <div className="dashboard-chart-area-wrap">
                        <button className="dashboard-chart-nav dashboard-chart-nav-left" type="button" aria-label="Previous">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                                <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                            </svg>
                        </button>
                        <div className="dashboard-chart-area">
                            <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart
                            data={chartDataForDisplay}
                            margin={{ top: 10, right: 40, left: 10, bottom: 0 }}
                        >
                            <defs>
                                <linearGradient id="visitorGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#8dcdff" stopOpacity={0.5} />
                                    <stop offset="20%" stopColor="#8dcdff" stopOpacity={0.25} />
                                    <stop offset="40%" stopColor="#8dcdff" stopOpacity={0.1} />
                                    <stop offset="50%" stopColor="#8dcdff" stopOpacity={0.05} />
                                    <stop offset="100%" stopColor="#8dcdff" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} vertical={false} />
                            <XAxis
                                dataKey="date"
                                tick={{ fontSize: 12, fill: chartTickColor }}
                                axisLine={{ stroke: chartAxisColor }}
                                tickLine={false}
                                interval={dateRange === 'today' || dateRange === 'yesterday' || dateRange === 'last-24'
                                    ? 2 // hourly: show every 3rd label (12am, 3am, 6am, ...)
                                    : chartDataForDisplay.length > 14
                                        ? Math.floor(chartDataForDisplay.length / 7) - 1 // weekly+ : ~7 labels
                                        : 0 // daily short range: show all labels
                                }
                                tickFormatter={(value) => value || ''}
                            />
                            {dateRange === 'today' || dateRange === 'yesterday' || dateRange === 'last-24' ? (
                                <>
                                    <YAxis
                                        yAxisId="left"
                                        tick={{ fontSize: 12, fill: chartTickColor }}
                                        axisLine={{ stroke: chartAxisColor }}
                                        tickLine={{ stroke: chartAxisColor }}
                                        domain={[0, 'auto']}
                                        allowDecimals={false}
                                    />
                                    <YAxis
                                        yAxisId="right"
                                        orientation="right"
                                        tick={{ fontSize: 12, fill: chartTickColor }}
                                        axisLine={{ stroke: chartAxisColor }}
                                        tickLine={false}
                                        domain={[0, 'auto']}
                                        tickFormatter={(v) => `$${v}`}
                                    />
                                </>
                            ) : (
                                <>
                                    <YAxis yAxisId="left" tick={{ fontSize: 12, fill: chartTickColor }} axisLine={false} tickLine={false} domain={[0, 'auto']} allowDecimals={false} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(1)}k` : v} />
                                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12, fill: chartTickColor }} axisLine={false} tickLine={false} domain={[0, 'auto']} tickFormatter={(v) => `$${v.toLocaleString()}`} />
                                </>
                            )}
                            <Tooltip
                                content={<ChartTooltip />}
                                cursor={{ stroke: dashTheme === 'dark' ? '#64748B' : '#374151', strokeWidth: 1, strokeDasharray: 'none' }}
                                wrapperStyle={{ outline: 'none' }}
                                contentStyle={{
                                    background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
                                    border: '1px solid rgba(255,255,255,0.08)',
                                    borderRadius: '1rem',
                                    padding: 0,
                                    boxShadow: '0 12px 32px rgba(0,0,0,0.35)'
                                }}
                            />
                            {(dateRange === 'today' || dateRange === 'yesterday' || dateRange === 'last-24') ? (
                                <>
                                    <Area yAxisId="left" type="monotone" dataKey="visitors" fill="url(#visitorGradient)" fillOpacity={0.6} stroke="none" isAnimationActive animationDuration={800} />
                                    <Line yAxisId="left" type="monotone" dataKey="visitors" stroke="#8dcdff" strokeWidth={3} strokeLinecap="round" dot={<HourlyChartDot />} activeDot={{ r: 5, fill: '#fff', stroke: '#374151', strokeWidth: 1.5 }} name="Visitors" isAnimationActive animationDuration={800} />
                                </>
                            ) : (
                                <>
                                    <Bar yAxisId="left" dataKey="visitors" fill="#F4A58A" radius={[4, 4, 0, 0]} name="Visitors" isAnimationActive animationDuration={800} />
                                    <Line yAxisId="right" type="monotone" dataKey="revenue" stroke="#3B82F6" strokeWidth={2.5} dot={<ChartDot />} activeDot={{ r: 8 }} name="Revenue" isAnimationActive animationDuration={800} />
                                </>
                            )}
                            </ComposedChart>
                        </ResponsiveContainer>
                        </div>
                        <button className="dashboard-chart-nav dashboard-chart-nav-right" type="button" aria-label="Next">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                                <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                            </svg>
                        </button>
                    </div>
                        </>
                    )}
                </section>

                <div className="dashboard-grid-2" style={{ position: 'relative' }}>
                    <div style={{ gridColumn: '1 / -1', display: 'contents' }}>
                    <div className="dashboard-section-card dashboard-acquisition-card">
                        <div className="dashboard-acquisition-header">
                            <div className="dashboard-section-tabs">
                                {['Channel', 'Referrer', 'Campaign', 'Keyword'].map(t => (
                                    <button key={t} type="button" className={referrerTab === t ? 'active' : ''} onClick={() => setReferrerTab(t)}>{t}</button>
                                ))}
                            </div>
                            <div className="dashboard-acquisition-controls">
                                {referrerTab === 'Channel' && (
                                    <div className="dashboard-filter-dropdown" ref={channelDropdownRef}>
                                        <button
                                            type="button"
                                            className="dashboard-acquisition-select dashboard-filter-trigger"
                                            onClick={() => setChannelDropdownOpen(o => !o)}
                                            aria-expanded={channelDropdownOpen}
                                            aria-haspopup="listbox"
                                            aria-label="Filter by channel"
                                        >
                                            {!channelFilter || channelFilter === 'All'
                                                ? (acquisitionMetric === 'Revenue'
                                                    ? `All (${formatRevenue(TOTAL_CHANNEL_REVENUE)})`
                                                    : `All (${formatCount(TOTAL_CHANNEL_VISITORS)})`)
                                                : (() => {
                                                    const ch = channelsData.find(c => c.name === channelFilter);
                                                    return acquisitionMetric === 'Revenue'
                                                        ? `${channelFilter} (${formatRevenue(ch?.revenue ?? 0)})`
                                                        : `${channelFilter} (${formatCount(ch?.visitors ?? 0)})`;
                                                })()}
                                            <span className="dashboard-filter-chevron">{channelDropdownOpen ? '▲' : '▼'}</span>
                                        </button>
                                        {channelDropdownOpen && (
                                            <ul className="dashboard-filter-menu" role="listbox">
                                                <li role="option" aria-selected={!channelFilter || channelFilter === 'All'}>
                                                    <button type="button" onClick={() => handleChannelFilter('All')}>
                                                        All ({acquisitionMetric === 'Revenue' ? formatRevenue(TOTAL_CHANNEL_REVENUE) : formatCount(TOTAL_CHANNEL_VISITORS)})
                                                    </button>
                                                </li>
                                                {channelsData.map(c => (
                                                    <li key={c.name} role="option" aria-selected={channelFilter === c.name}>
                                                        <button type="button" onClick={() => handleChannelFilter(c.name)}>
                                                            {c.name} ({acquisitionMetric === 'Revenue' ? formatRevenue(c.revenue) : formatCount(c.visitors)})
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                )}
                                {referrerTab === 'Campaign' && (
                                    <div className="dashboard-filter-dropdown" ref={campaignDropdownRef}>
                                        <button
                                            type="button"
                                            className="dashboard-acquisition-select dashboard-filter-trigger"
                                            onClick={() => setCampaignDropdownOpen(o => !o)}
                                            aria-expanded={campaignDropdownOpen}
                                            aria-haspopup="listbox"
                                            aria-label="Filter by campaign"
                                        >
                                            {!campaignFilter || campaignFilter === 'All'
                                                ? (acquisitionMetric === 'Revenue'
                                                    ? `All (${formatRevenue(TOTAL_CAMPAIGN_REVENUE)})`
                                                    : `All (${formatCount(TOTAL_CAMPAIGN_VISITORS)})`)
                                                : (() => {
                                                    const camp = campaignsData.find(c => c.utm === campaignFilter);
                                                    const label = camp ? (camp.utm.length > 25 ? camp.utm.slice(0, 25) + '...' : camp.utm) : campaignFilter;
                                                    const val = acquisitionMetric === 'Revenue' ? formatRevenue(camp?.revenue ?? 0) : formatCount(camp?.visitors ?? 0);
                                                    return `${label} (${val})`;
                                                })()}
                                            <span className="dashboard-filter-chevron">{campaignDropdownOpen ? '▲' : '▼'}</span>
                                        </button>
                                        {campaignDropdownOpen && (
                                            <ul className="dashboard-filter-menu" role="listbox">
                                                <li role="option" aria-selected={!campaignFilter || campaignFilter === 'All'}>
                                                    <button type="button" onClick={() => handleCampaignFilter('All')}>
                                                        All ({acquisitionMetric === 'Revenue' ? formatRevenue(TOTAL_CAMPAIGN_REVENUE) : formatCount(TOTAL_CAMPAIGN_VISITORS)})
                                                    </button>
                                                </li>
                                                {campaignsData.map(c => (
                                                    <li key={c.utm} role="option" aria-selected={campaignFilter === c.utm}>
                                                        <button type="button" onClick={() => handleCampaignFilter(c.utm)} title={c.utm}>
                                                            {c.utm.length > 40 ? c.utm.slice(0, 40) + '...' : c.utm} ({acquisitionMetric === 'Revenue' ? formatRevenue(c.revenue) : formatCount(c.visitors)})
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                )}
                                <button
                                    type="button"
                                    className="dashboard-metric-filter active"
                                    onClick={() => setAcquisitionMetric(m => m === 'Visitors' ? 'Revenue' : 'Visitors')}
                                    aria-pressed="true"
                                    aria-label={acquisitionMetric === 'Visitors' ? 'Showing Visitors; click to switch to Revenue' : 'Showing Revenue; click to switch to Visitors'}
                                >
                                    {acquisitionMetric === 'Visitors' ? 'Visitors' : 'Revenue'}
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className={`dashboard-metric-icon ${acquisitionMetric === 'Revenue' ? 'flipped' : ''}`}><path fillRule="evenodd" d="M13.78 10.47a.75.75 0 0 1 0 1.06l-2.25 2.25a.75.75 0 0 1-1.06 0l-2.25-2.25a.75.75 0 1 1 1.06-1.06l.97.97V5.75a.75.75 0 0 1 1.5 0v5.69l.97-.97a.75.75 0 0 1 1.06 0ZM2.22 5.53a.75.75 0 0 1 0-1.06l2.25-2.25a.75.75 0 0 1 1.06 0l2.25 2.25a.75.75 0 0 1-1.06 1.06l-.97-.97v5.69a.75.75 0 0 1-1.5 0V4.56l-.97.97a.75.75 0 0 1-1.06 0Z" clipRule="evenodd" /></svg>
                                </button>
                            </div>
                        </div>
                        {referrerTab === 'Channel' && (
                            <div className="dashboard-channel-donut-wrap">
                                <ResponsiveContainer width="100%" height={280}>
                                    <PieChart>
                                        <Tooltip content={<ChannelDonutTooltip />} />
                                        <Pie
                                            data={filteredChannels}
                                            dataKey={acquisitionMetric === 'Revenue' ? 'revenue' : 'visitors'}
                                            nameKey="name"
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={70}
                                            outerRadius={100}
                                            paddingAngle={1}
                                            label={({name, percent}) => percent >= 0.05 ? name : ''}
                                            isAnimationActive
                                            animationDuration={600}
                                            animationEasing="ease-out"
                                        >
                                            {filteredChannels.slice(0, CARD_LIST_LIMIT).map((entry, i) => (
                                                <Cell key={i} fill={entry.color} stroke="#fff" strokeWidth={1} />
                                            ))}
                                        </Pie>
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                        {referrerTab === 'Referrer' && (
                            <>
                                {referralsData.slice(0, CARD_LIST_LIMIT).map((r, i) => (
                                    <div
                                        key={i}
                                        className="dashboard-acquisition-row dashboard-acquisition-row-hover dashboard-referrer-row"
                                        onMouseMove={(e) => handleRowMouseMove(e, { type: 'referrer', ...r })}
                                        onMouseLeave={handleRowMouseLeave}
                                    >
                                        <div className="dashboard-list-label" style={{flex: 1, minWidth: 0}}>
                                            <span style={{width: '1.6rem', textAlign: 'center'}}>{r.icon}</span>
                                            {r.name}
                                        </div>
                                        <div className="dashboard-list-value-wrap">
                                            <span className="dashboard-list-value">
                                                {acquisitionMetric === 'Revenue'
                                                    ? formatRevenue(r.revenue)
                                                    : (r.visitors >= 1000 ? `${(r.visitors / 1000).toFixed(1)}k` : r.visitors)}
                                            </span>
                                            <div className="dashboard-row-actions">
                                                {r.externalUrl && (
                                                    <a href={r.externalUrl} target="_blank" rel="noopener noreferrer" className="dashboard-row-action-btn dashboard-external-link-btn" aria-label="Open external link" title="Open external link">
                                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" width="18" height="18"><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" /></svg>
                                                    </a>
                                                )}
                                                <button type="button" className="dashboard-row-action-btn dashboard-filter-btn" aria-label="Filter by this referrer" title="Filter by this referrer" onClick={() => addFilter('Referrer', r.name)}>
                                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" width="18" height="18"><path strokeLinecap="round" strokeLinejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 0 1-.659 1.591l-5.432 5.432a2.25 2.25 0 0 0-.659 1.591v2.927a2.25 2.25 0 0 1-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 0 0-.659-1.591L3.659 7.409A2.25 2.25 0 0 1 3 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0 1 12 3Z" /></svg>
                                                </button>
                                            </div>
                                        </div>
                                        {tooltipData?.type === 'referrer' && tooltipData?.name === r.name && (
                                            <div
                                                className="dashboard-row-tooltip dashboard-row-tooltip-visible"
                                                role="tooltip"
style={{
                                                    left: tooltipPos.x + (tooltipPos.side === 'left' ? -20 : 20),
                                                    top: tooltipPos.y + (tooltipPos.vertical === 'top' ? -20 : 20),
                                                    transform: `translate(${tooltipPos.side === 'left' ? '-100%' : '0%'}, ${tooltipPos.vertical === 'top' ? '-100%' : '0%'})`
                                                }}
                                            >
                                                <div className="dashboard-row-tooltip-header">
                                                    <span style={{marginRight: '0.5rem'}}>{r.icon}</span>
                                                    {r.name}
                                                </div>
                                                <div className="dashboard-row-tooltip-row"><span className="tooltip-swatch visitors" /> Visitors <strong>{r.visitors >= 1000 ? `${(r.visitors / 1000).toFixed(1)}k` : r.visitors}</strong></div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </>
                        )}
                        {referrerTab === 'Campaign' && (
                            <>
                                {filteredCampaigns.slice(0, CARD_LIST_LIMIT).map((c, i) => (
                                    <div key={i} className="dashboard-acquisition-row dashboard-acquisition-row-hover">
                                        <div className="dashboard-list-label dashboard-acquisition-label" style={{flex: 1, minWidth: 0}}>
                                            <span className="dashboard-acquisition-row-text">{c.utm.length > 40 ? c.utm.slice(0, 40) + '...' : c.utm}</span>
                                        </div>
                                        <div className="dashboard-list-value">
                                            {acquisitionMetric === 'Revenue' ? formatRevenue(c.revenue) : (c.visitors >= 1000 ? `${(c.visitors / 1000).toFixed(1)}k` : c.visitors)}
                                        </div>
                                        <div className="dashboard-acquisition-row-tooltip" role="tooltip">
                                            <div className="tooltip-line" style={{wordBreak: 'break-all'}}><strong>{c.utm}</strong></div>
                                            <div className="tooltip-row"><span className="swatch visitors" /> Visitors: <strong>{c.visitors.toLocaleString()}</strong></div>
                                            <div className="tooltip-row"><span className="swatch revenue" /> Revenue: <strong>${c.revenue >= 1000 ? `${(c.revenue / 1000).toFixed(1)}k` : c.revenue}</strong></div>
                                            <div className="tooltip-row">Revenue/visitor: <strong>${c.revenuePerVisitor.toFixed(2)}</strong></div>
                                            <div className="tooltip-row">Conversion rate: <strong>{c.conversionRate}%</strong></div>
                                        </div>
                                    </div>
                                ))}
                            </>
                        )}
                        {referrerTab === 'Keyword' && (
                            <>
                                {keywordsData.slice(0, CARD_LIST_LIMIT).map((k, i) => (
                                    <div key={i} className="dashboard-acquisition-row dashboard-acquisition-row-hover dashboard-referrer-row">
                                        <div className="dashboard-list-label" style={{flex: 1, minWidth: 0}}>
                                            <span style={{width: '1.6rem', textAlign: 'center', fontWeight: 600, color: '#4285F4'}}>G</span>
                                            {k.keyword}
                                        </div>
                                        <div className="dashboard-list-value">
                                            {acquisitionMetric === 'Revenue' ? formatRevenue(k.revenueEst) : k.visitors}
                                        </div>
                                        <div className="dashboard-list-bar">
                                            <div
                                                className="dashboard-list-bar-fill"
                                                style={{
                                                    width: `${acquisitionMetric === 'Revenue'
                                                        ? (maxKeywordRevenue ? (k.revenueEst / maxKeywordRevenue) * 100 : 0)
                                                        : (maxKeywordVisitors ? (k.visitors / maxKeywordVisitors) * 100 : 0)}%`
                                                }}
                                            />
                                        </div>
                                        <div className="dashboard-acquisition-row-tooltip dashboard-keyword-tooltip" role="tooltip">
                                            <div className="tooltip-date">{k.keyword}</div>
                                            <div className="tooltip-row"><span className="swatch visitors" /> Visitors: <strong>{k.visitors}</strong></div>
                                            <div className="tooltip-row"><span className="tooltip-swatch brown" /> Revenue (est.): <strong>${k.revenueEst >= 1000 ? `${(k.revenueEst / 1000).toFixed(1)}k` : k.revenueEst}</strong></div>
                                            <div className="tooltip-row">🏆 Position: <strong>{k.position}{k.position === 1 ? 'st' : k.position === 2 ? 'nd' : k.position === 3 ? 'rd' : 'th'}</strong></div>
                                            <div className="tooltip-row">Impressions: <strong>{k.impressions.toLocaleString()}</strong></div>
                                            <div className="tooltip-row">Clicks: <strong>{k.clicks}</strong></div>
                                            <div className="tooltip-row">CTR: <strong>{k.ctr}%</strong></div>
                                        </div>
                                    </div>
                                ))}
                            </>
                        )}
                        <div className="dashboard-details-btn-wrap">
                            <button
                                type="button"
                                className="dashboard-details-btn"
                                onClick={() => {
                                    if (referrerTab === 'Channel') setChannelDetailModalOpen(true);
                                    if (referrerTab === 'Referrer') setReferrerDetailModalOpen(true);
                                    if (referrerTab === 'Campaign') setCampaignDetailModalOpen(true);
                                    if (referrerTab === 'Keyword') setKeywordDetailModalOpen(true);
                                }}
                            >
                                <DetailsExpandIcon />
                                DETAILS
                            </button>
                        </div>
                    </div>

                    {channelDetailModalOpen && (
                        <div className="dashboard-modal-overlay" onClick={() => setChannelDetailModalOpen(false)} role="dialog" aria-modal="true" aria-labelledby="channel-detail-title">
                            <div className="dashboard-modal dashboard-channel-detail-modal" onClick={e => e.stopPropagation()}>
                                <div className="dashboard-modal-header">
                                    <h2 id="channel-detail-title" className="dashboard-modal-title">Channel</h2>
                                    <input
                                        type="search"
                                        className="dashboard-modal-search"
                                        placeholder="Search..."
                                        value={channelDetailSearch}
                                        onChange={e => setChannelDetailSearch(e.target.value)}
                                        aria-label="Search channels"
                                    />
                                    <button
                                        type="button"
                                        className="dashboard-modal-close"
                                        onClick={() => setChannelDetailModalOpen(false)}
                                        aria-label="Close"
                                    >
                                        ×
                                    </button>
                                </div>
                                <div className="dashboard-modal-body">
                                    <table className="dashboard-detail-table">
                                        <thead>
                                            <tr>
                                                <th>Channel</th>
                                                <th>Visitors</th>
                                                <th>Revenue</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {channelsData.filter(c =>
                                                !channelDetailSearch.trim() || c.name.toLowerCase().includes(channelDetailSearch.trim().toLowerCase())
                                            ).map((c, i) => (
                                                <tr key={i}>
                                                    <td>{c.name}</td>
                                                    <td>{c.visitors >= 1000 ? `${(c.visitors / 1000).toFixed(1)}k` : c.visitors}</td>
                                                    <td>${c.revenue.toLocaleString()}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}

                    {referrerDetailModalOpen && (
                        <div className="dashboard-modal-overlay" onClick={() => setReferrerDetailModalOpen(false)} role="dialog" aria-modal="true" aria-labelledby="referrer-detail-title">
                            <div className="dashboard-modal dashboard-channel-detail-modal" onClick={e => e.stopPropagation()}>
                                <div className="dashboard-modal-header">
                                    <h2 id="referrer-detail-title" className="dashboard-modal-title">Referrer</h2>
                                    <input
                                        type="search"
                                        className="dashboard-modal-search"
                                        placeholder="Search..."
                                        value={referrerDetailSearch}
                                        onChange={e => setReferrerDetailSearch(e.target.value)}
                                        aria-label="Search referrers"
                                    />
                                    <button
                                        type="button"
                                        className="dashboard-modal-close"
                                        onClick={() => setReferrerDetailModalOpen(false)}
                                        aria-label="Close"
                                    >
                                        ×
                                    </button>
                                </div>
                                <div className="dashboard-modal-body">
                                    <table className="dashboard-detail-table">
                                        <thead>
                                            <tr>
                                                <th>Referrer</th>
                                                <th>Visitors</th>
                                                <th>Revenue</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {referralsData.filter(r =>
                                                !referrerDetailSearch.trim() || r.name.toLowerCase().includes(referrerDetailSearch.trim().toLowerCase())
                                            ).map((r, i) => (
                                                <tr key={i}>
                                                    <td>
                                                        <span className="dashboard-detail-referrer-cell">
                                                            <span className="dashboard-detail-referrer-icon" aria-hidden>{r.icon}</span>
                                                            {r.name}
                                                        </span>
                                                    </td>
                                                    <td>{r.visitors >= 1000 ? `${(r.visitors / 1000).toFixed(1)}k` : r.visitors}</td>
                                                    <td>${r.revenue.toLocaleString()}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}

                    {campaignDetailModalOpen && (
                        <div className="dashboard-modal-overlay" onClick={() => setCampaignDetailModalOpen(false)} role="dialog" aria-modal="true" aria-labelledby="campaign-detail-title">
                            <div className="dashboard-modal dashboard-channel-detail-modal" onClick={e => e.stopPropagation()}>
                                <div className="dashboard-modal-header">
                                    <h2 id="campaign-detail-title" className="dashboard-modal-title">Campaign</h2>
                                    <input
                                        type="search"
                                        className="dashboard-modal-search"
                                        placeholder="Search..."
                                        value={campaignDetailSearch}
                                        onChange={e => setCampaignDetailSearch(e.target.value)}
                                        aria-label="Search campaigns"
                                    />
                                    <button
                                        type="button"
                                        className="dashboard-modal-close"
                                        onClick={() => setCampaignDetailModalOpen(false)}
                                        aria-label="Close"
                                    >
                                        ×
                                    </button>
                                </div>
                                <div className="dashboard-modal-body">
                                    <table className="dashboard-detail-table">
                                        <thead>
                                            <tr>
                                                <th>Campaign (All)</th>
                                                <th>Visitors</th>
                                                <th>Revenue</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {campaignsData.filter(c =>
                                                !campaignDetailSearch.trim() || c.utm.toLowerCase().includes(campaignDetailSearch.trim().toLowerCase())
                                            ).map((c, i) => (
                                                <tr key={i}>
                                                    <td className="dashboard-detail-campaign-cell" title={c.utm}>{c.utm}</td>
                                                    <td>{c.visitors >= 1000 ? `${(c.visitors / 1000).toFixed(1)}k` : c.visitors}</td>
                                                    <td>${c.revenue.toLocaleString()}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}

                    {keywordDetailModalOpen && (
                        <div className="dashboard-modal-overlay" onClick={() => setKeywordDetailModalOpen(false)} role="dialog" aria-modal="true" aria-labelledby="keyword-detail-title">
                            <div className="dashboard-modal dashboard-channel-detail-modal dashboard-keyword-detail-modal" onClick={e => e.stopPropagation()}>
                                <div className="dashboard-modal-header">
                                    <h2 id="keyword-detail-title" className="dashboard-modal-title dashboard-keyword-modal-title">
                                        <span className="dashboard-keyword-modal-logo" aria-hidden>G</span>
                                        Google Search Console
                                    </h2>
                                    <input
                                        type="search"
                                        className="dashboard-modal-search"
                                        placeholder="Search..."
                                        value={keywordDetailSearch}
                                        onChange={e => setKeywordDetailSearch(e.target.value)}
                                        aria-label="Search keywords"
                                    />
                                    <button
                                        type="button"
                                        className="dashboard-modal-close"
                                        onClick={() => setKeywordDetailModalOpen(false)}
                                        aria-label="Close"
                                    >
                                        ×
                                    </button>
                                </div>
                                <div className="dashboard-modal-body">
                                    <table className="dashboard-detail-table dashboard-keyword-detail-table">
                                        <thead>
                                            <tr>
                                                <th>Search Term</th>
                                                <th>Position</th>
                                                <th>Impressions</th>
                                                <th>Visitors</th>
                                                <th>CTR</th>
                                                <th>Revenue (est.)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {keywordsData.filter(k =>
                                                !keywordDetailSearch.trim() || k.keyword.toLowerCase().includes(keywordDetailSearch.trim().toLowerCase())
                                            ).map((k, i) => (
                                                <tr key={i}>
                                                    <td>
                                                        <span className="dashboard-detail-referrer-cell">
                                                            <span className="dashboard-detail-keyword-g-icon" aria-hidden>G</span>
                                                            {k.keyword}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className="dashboard-detail-position-cell">
                                                            {k.position <= 3 && <span className="dashboard-detail-trophy" aria-hidden>🏆</span>}
                                                            {k.position}{k.position === 1 ? 'st' : k.position === 2 ? 'nd' : k.position === 3 ? 'rd' : 'th'}
                                                        </span>
                                                    </td>
                                                    <td>{k.impressions >= 1000 ? `${(k.impressions / 1000).toFixed(1)}k` : k.impressions.toLocaleString()}</td>
                                                    <td>{k.visitors}</td>
                                                    <td>{k.ctr}%</td>
                                                    <td>${k.revenueEst.toLocaleString()}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}

                    {techDetailModalOpen && (
                        <div className="dashboard-modal-overlay" onClick={() => setTechDetailModalOpen(false)} role="dialog" aria-modal="true" aria-labelledby="tech-detail-title">
                            <div className="dashboard-modal dashboard-channel-detail-modal" onClick={e => e.stopPropagation()}>
                                <div className="dashboard-modal-header">
                                    <h2 id="tech-detail-title" className="dashboard-modal-title">{techTab}</h2>
                                    <input
                                        type="search"
                                        className="dashboard-modal-search"
                                        placeholder="Search..."
                                        value={techDetailSearch}
                                        onChange={e => setTechDetailSearch(e.target.value)}
                                        aria-label={`Search ${techTab.toLowerCase()}`}
                                    />
                                    <button
                                        type="button"
                                        className="dashboard-modal-close"
                                        onClick={() => setTechDetailModalOpen(false)}
                                        aria-label="Close"
                                    >
                                        ×
                                    </button>
                                </div>
                                <div className="dashboard-modal-body">
                                    <table className="dashboard-detail-table">
                                        <thead>
                                            <tr>
                                                <th>{techTab}</th>
                                                <th>Visitors</th>
                                                <th>Revenue</th>
                                                <th>Revenue/visitor</th>
                                                <th>Conversion rate</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(techTab === 'Browser' ? browsersData : techTab === 'OS' ? osListData : devicesData)
                                                .filter(item => !techDetailSearch.trim() || item.name.toLowerCase().includes(techDetailSearch.trim().toLowerCase()))
                                                .map((item, i) => (
                                                    <tr key={i}>
                                                        <td>
                                                            <span className="dashboard-detail-referrer-cell">
                                                                <span className="dashboard-detail-referrer-icon" aria-hidden>{item.icon}</span>
                                                                {item.name}
                                                            </span>
                                                        </td>
                                                        <td>{item.visitors}</td>
                                                        <td>${item.revenue}</td>
                                                        <td>${item.revenuePerVisitor.toFixed(2)}</td>
                                                        <td>{item.conversionRate}%</td>
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}

                    {hostDetailModalOpen && (
                        <div className="dashboard-modal-overlay" onClick={() => setHostDetailModalOpen(false)} role="dialog" aria-modal="true" aria-labelledby="host-detail-title">
                            <div className="dashboard-modal dashboard-channel-detail-modal" onClick={e => e.stopPropagation()}>
                                <div className="dashboard-modal-header">
                                    <h2 id="host-detail-title" className="dashboard-modal-title">{hostTab}</h2>
                                    <input
                                        type="search"
                                        className="dashboard-modal-search"
                                        placeholder="Search..."
                                        value={hostDetailSearch}
                                        onChange={e => setHostDetailSearch(e.target.value)}
                                        aria-label={`Search ${hostTab.toLowerCase()}`}
                                    />
                                    <button
                                        type="button"
                                        className="dashboard-modal-close"
                                        onClick={() => setHostDetailModalOpen(false)}
                                        aria-label="Close"
                                    >
                                        ×
                                    </button>
                                </div>
                                <div className="dashboard-modal-body">
                                    <table className="dashboard-detail-table">
                                        <thead>
                                            <tr>
                                                <th>{hostTab}</th>
                                                <th>Visitors</th>
                                                <th>Revenue</th>
                                                <th>Revenue/visitor</th>
                                                <th>Conversion rate</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(hostTab === 'Hostname' ? hostnamesData : hostTab === 'Page' ? pagesData : hostTab === 'Entry page' ? entryPagesData : exitLinksData)
                                                .filter(item => !hostDetailSearch.trim() || item.name.toLowerCase().includes(hostDetailSearch.trim().toLowerCase()))
                                                .map((item, i) => (
                                                    <tr key={i}>
                                                        <td>
                                                            <span className="dashboard-detail-referrer-cell">
                                                                {item.icon != null && (
                                                                    <span className="dashboard-detail-referrer-icon dashboard-exit-link-icon" aria-hidden>{item.icon}</span>
                                                                )}
                                                                {item.name}
                                                            </span>
                                                        </td>
                                                        <td>{item.visitors}</td>
                                                        <td>${item.revenue}</td>
                                                        <td>${item.revenuePerVisitor != null ? item.revenuePerVisitor.toFixed(2) : (item.visitors > 0 ? (item.revenue / item.visitors).toFixed(2) : '0.00')}</td>
                                                        <td>{item.conversionRate != null ? item.conversionRate : 0}%</td>
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="dashboard-section-card dashboard-geo-card">
                        <div className="dashboard-geo-header">
                            <div className="dashboard-section-tabs">
                                {['Map', 'Country', 'Region', 'City'].map(t => (
                                    <button key={t} type="button" className={geoTab === t ? 'active' : ''} onClick={() => setGeoTab(t)}>{t}</button>
                                ))}
                            </div>
                            {geoTab !== 'Map' && (
                                <div className="dashboard-geo-controls">
                                    <button
                                        type="button"
                                        className="dashboard-metric-filter active"
                                        onClick={() => setGeoMetric(m => m === 'Visitors' ? 'Revenue' : 'Visitors')}
                                        aria-pressed={geoMetric === 'Visitors'}
                                        aria-label={geoMetric === 'Visitors' ? 'Showing Visitors; click to switch to Revenue' : 'Showing Revenue; click to switch to Visitors'}
                                    >
                                        {geoMetric === 'Visitors' ? 'Visitors' : 'Revenue'}
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className={`dashboard-metric-icon ${geoMetric === 'Revenue' ? 'flipped' : ''}`}><path fillRule="evenodd" d="M13.78 10.47a.75.75 0 0 1 0 1.06l-2.25 2.25a.75.75 0 0 1-1.06 0l-2.25-2.25a.75.75 0 1 1 1.06-1.06l.97.97V5.75a.75.75 0 0 1 1.5 0v5.69l.97-.97a.75.75 0 0 1 1.06 0ZM2.22 5.53a.75.75 0 0 1 0-1.06l2.25-2.25a.75.75 0 0 1 1.06 0l2.25 2.25a.75.75 0 0 1-1.06 1.06l-.97-.97v5.69a.75.75 0 0 1-1.5 0V4.56l-.97.97a.75.75 0 0 1-1.06 0Z" clipRule="evenodd" /></svg>
                                    </button>
                                </div>
                            )}
                        </div>
                        {geoTab === 'Map' && (
                            <GeoChoroplethMap geoMetric={geoMetric} countries={countriesData} showCountryTooltip isDark={dashTheme === 'dark'} />
                        )}
                        {geoTab === 'Country' && (
                            <>
                                {countriesData.slice(0, CARD_LIST_LIMIT).map((c, i) => (
                                    <div
                                        key={i}
                                        className="dashboard-list-item dashboard-geo-row dashboard-geo-row-hover"
                                        onMouseMove={(e) => handleRowMouseMove(e, { type: 'country', ...c })}
                                        onMouseLeave={handleRowMouseLeave}
                                    >
                                        <div className="dashboard-list-label">
                                            <img src={getCountryFlagUrl(c.code)} alt={c.name} className="dashboard-geo-flag-img" />
                                            {c.name}
                                        </div>
                                        <div className="dashboard-list-value-wrap">
                                            <span className="dashboard-list-value">
                                                {geoMetric === 'Revenue'
                                                    ? formatRevenue(c.revenue)
                                                    : (c.visitors >= 1000 ? `${(c.visitors / 1000).toFixed(1)}k` : c.visitors)}
                                            </span>
                                            <div className="dashboard-row-actions">
                                                <button type="button" className="dashboard-row-action-btn dashboard-filter-btn" aria-label="Filter by this country" title="Filter by this country" onClick={() => addFilter('Country', c.name, c.code)}>
                                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" width="18" height="18"><path strokeLinecap="round" strokeLinejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 0 1-.659 1.591l-5.432 5.432a2.25 2.25 0 0 0-.659 1.591v2.927a2.25 2.25 0 0 1-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 0 0-.659-1.591L3.659 7.409A2.25 2.25 0 0 1 3 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0 1 12 3Z" /></svg>
                                                </button>
                                            </div>
                                        </div>
                                        {tooltipData?.type === 'country' && tooltipData?.name === c.name && (
                                            <div
                                                className="dashboard-row-tooltip dashboard-row-tooltip-visible"
                                                role="tooltip"
style={{
                                                    left: tooltipPos.x + (tooltipPos.side === 'left' ? -20 : 20),
                                                    top: tooltipPos.y + (tooltipPos.vertical === 'top' ? -20 : 20),
                                                    transform: `translate(${tooltipPos.side === 'left' ? '-100%' : '0%'}, ${tooltipPos.vertical === 'top' ? '-100%' : '0%'})`
                                                }}
                                            >
                                                <div className="dashboard-row-tooltip-header">
                                                    <img src={getCountryFlagUrl(c.code)} alt={c.name} className="dashboard-geo-flag-img" />
                                                    {c.name}
                                                </div>
                                                <div className="dashboard-row-tooltip-row"><span className="tooltip-swatch visitors" /> Visitors <strong>{c.visitors}</strong></div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </>
                        )}
                        {geoTab === 'Region' && (
                            <>
                                {regionsData.slice(0, CARD_LIST_LIMIT).map((r, i) => (
                                    <div
                                        key={i}
                                        className={`dashboard-list-item dashboard-geo-row dashboard-geo-row-hover ${i % 2 === 1 ? 'dashboard-geo-row-alt' : ''}`}
                                        onMouseMove={(e) => handleRowMouseMove(e, { type: 'region', ...r })}
                                        onMouseLeave={handleRowMouseLeave}
                                    >
                                        <div className="dashboard-list-label">
                                            <img src={getCountryFlagUrl(r.code)} alt={r.name} className="dashboard-geo-flag-img" />
                                            {r.name}
                                        </div>
                                        <div className="dashboard-list-value-wrap">
                                            <span className="dashboard-list-value">
                                                {geoMetric === 'Revenue'
                                                    ? formatRevenue(r.revenue)
                                                    : (r.visitors >= 1000 ? `${(r.visitors / 1000).toFixed(1)}k` : r.visitors)}
                                            </span>
                                            <div className="dashboard-row-actions">
                                                <button type="button" className="dashboard-row-action-btn dashboard-filter-btn" aria-label="Filter by this region" title="Filter by this region" onClick={() => addFilter('Region', r.name, r.code)}>
                                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" width="18" height="18"><path strokeLinecap="round" strokeLinejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 0 1-.659 1.591l-5.432 5.432a2.25 2.25 0 0 0-.659 1.591v2.927a2.25 2.25 0 0 1-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 0 0-.659-1.591L3.659 7.409A2.25 2.25 0 0 1 3 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0 1 12 3Z" /></svg>
                                                </button>
                                            </div>
                                        </div>
                                        {tooltipData?.type === 'region' && tooltipData?.name === r.name && (
                                            <div
                                                className="dashboard-row-tooltip dashboard-row-tooltip-visible"
                                                role="tooltip"
style={{
                                                    left: tooltipPos.x + (tooltipPos.side === 'left' ? -20 : 20),
                                                    top: tooltipPos.y + (tooltipPos.vertical === 'top' ? -20 : 20),
                                                    transform: `translate(${tooltipPos.side === 'left' ? '-100%' : '0%'}, ${tooltipPos.vertical === 'top' ? '-100%' : '0%'})`
                                                }}
                                            >
                                                <div className="dashboard-row-tooltip-header">
                                                    <img src={getCountryFlagUrl(r.code)} alt={r.name} className="dashboard-geo-flag-img" />
                                                    {r.name}
                                                </div>
                                                <div className="dashboard-row-tooltip-row"><span className="tooltip-swatch visitors" /> Visitors <strong>{r.visitors}</strong></div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </>
                        )}
                        {geoTab === 'City' && (
                            <>
                                {citiesData.slice(0, CARD_LIST_LIMIT).map((c, i) => (
                                    <div
                                        key={i}
                                        className="dashboard-list-item dashboard-geo-row dashboard-geo-row-hover"
                                        onMouseMove={(e) => handleRowMouseMove(e, { type: 'city', ...c })}
                                        onMouseLeave={handleRowMouseLeave}
                                    >
                                        <div className="dashboard-list-label">
                                            <img src={getCountryFlagUrl(c.code)} alt={c.name} className="dashboard-geo-flag-img" />
                                            {c.name}
                                        </div>
                                        <div className="dashboard-list-value-wrap">
                                            <span className="dashboard-list-value">
                                                {geoMetric === 'Revenue'
                                                    ? formatRevenue(c.revenue)
                                                    : (c.visitors >= 1000 ? `${(c.visitors / 1000).toFixed(1)}k` : c.visitors)}
                                            </span>
                                            <div className="dashboard-row-actions">
                                                <button type="button" className="dashboard-row-action-btn dashboard-filter-btn" aria-label="Filter by this city" title="Filter by this city" onClick={() => addFilter('City', c.name, c.code)}>
                                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" width="18" height="18"><path strokeLinecap="round" strokeLinejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 0 1-.659 1.591l-5.432 5.432a2.25 2.25 0 0 0-.659 1.591v2.927a2.25 2.25 0 0 1-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 0 0-.659-1.591L3.659 7.409A2.25 2.25 0 0 1 3 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0 1 12 3Z" /></svg>
                                                </button>
                                            </div>
                                        </div>
                                        {tooltipData?.type === 'city' && tooltipData?.name === c.name && (
                                            <div
                                                className="dashboard-row-tooltip dashboard-row-tooltip-visible"
                                                role="tooltip"
style={{
                                                    left: tooltipPos.x + (tooltipPos.side === 'left' ? -20 : 20),
                                                    top: tooltipPos.y + (tooltipPos.vertical === 'top' ? -20 : 20),
                                                    transform: `translate(${tooltipPos.side === 'left' ? '-100%' : '0%'}, ${tooltipPos.vertical === 'top' ? '-100%' : '0%'})`
                                                }}
                                            >
                                                <div className="dashboard-row-tooltip-header">
                                                    <img src={getCountryFlagUrl(c.code)} alt={c.name} className="dashboard-geo-flag-img" />
                                                    {c.name}
                                                </div>
                                                <div className="dashboard-row-tooltip-row"><span className="tooltip-swatch visitors" /> Visitors <strong>{c.visitors}</strong></div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </>
                        )}
                        {geoTab !== 'Map' && (
                            <div className="dashboard-details-btn-wrap">
                                <button
                                    type="button"
                                    className="dashboard-details-btn"
                                    onClick={() => setGeoDetailModalOpen(true)}
                                >
                                    <DetailsExpandIcon />
                                    DETAILS
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {geoDetailModalOpen && (
                    <div className="dashboard-modal-overlay" onClick={() => setGeoDetailModalOpen(false)} role="dialog" aria-modal="true" aria-labelledby="geo-detail-title">
                        <div className="dashboard-modal dashboard-channel-detail-modal" onClick={e => e.stopPropagation()}>
                            <div className="dashboard-modal-header">
                                <h2 id="geo-detail-title" className="dashboard-modal-title">{geoTab}</h2>
                                <input
                                    type="search"
                                    className="dashboard-modal-search"
                                    placeholder="Search..."
                                    value={geoDetailSearch}
                                    onChange={e => setGeoDetailSearch(e.target.value)}
                                    aria-label={`Search ${geoTab.toLowerCase()}`}
                                />
                                <button
                                    type="button"
                                    className="dashboard-modal-close"
                                    onClick={() => setGeoDetailModalOpen(false)}
                                    aria-label="Close"
                                >
                                    ×
                                </button>
                            </div>
                            <div className="dashboard-modal-body">
                                <table className="dashboard-detail-table">
                                    <thead>
                                        <tr>
                                            <th>{geoTab}</th>
                                            <th>Visitors</th>
                                            <th>Revenue</th>
                                            <th>Revenue/visitor</th>
                                            <th>Conversion rate</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(geoTab === 'Country' ? countriesData : geoTab === 'Region' ? regionsData : citiesData)
                                            .filter(item => !geoDetailSearch.trim() || item.name.toLowerCase().includes(geoDetailSearch.trim().toLowerCase()))
                                            .map((item, i) => (
                                                <tr key={i}>
                                                    <td>
                                                        <span className="dashboard-detail-referrer-cell">
                                                            <img src={getCountryFlagUrl(item.code)} alt={item.name} className="dashboard-geo-flag-img" />
                                                            {item.name}
                                                        </span>
                                                    </td>
                                                    <td>{item.visitors}</td>
                                                    <td>${item.revenue}</td>
                                                    <td>${item.revenuePerVisitor != null ? item.revenuePerVisitor.toFixed(2) : (item.visitors > 0 ? (item.revenue / item.visitors).toFixed(2) : '0.00')}</td>
                                                    <td>{item.conversionRate != null ? item.conversionRate : 0}%</td>
                                                </tr>
                                            ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}



                    </div>

                    {/* Host & Technology: two separate grid sections with their own APIs */}
                    <section className="dashboard-host-tech-row" aria-label="Host and technology">
                        <h3 className="dashboard-section-heading dashboard-host-tech-heading">Host & technology</h3>
                        <div className="dashboard-grid-2 dashboard-host-tech-grid">
                    <div className="dashboard-section-card dashboard-tech-card dashboard-host-card" aria-label="Hostname, page, entry page, and exit link">
                        <div className="dashboard-tech-header">
                            <div className="dashboard-section-tabs">
                                {['Hostname', 'Page', 'Entry page', 'Exit link'].map(t => (
                                    <button key={t} type="button" className={hostTab === t ? 'active' : ''} onClick={() => setHostTab(t)}>{t}</button>
                                ))}
                            </div>
                            <button
                                type="button"
                                className="dashboard-metric-filter active"
                                onClick={() => setHostMetric(m => m === 'Visitors' ? 'Revenue' : 'Visitors')}
                                aria-pressed={hostMetric === 'Visitors'}
                                aria-label={hostMetric === 'Visitors' ? 'Sort by Visitors; click to sort by Revenue' : 'Sort by Revenue; click to sort by Visitors'}
                            >
                                {hostMetric === 'Visitors' ? 'Visitors' : 'Revenue'}
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className={`dashboard-metric-icon ${hostMetric === 'Revenue' ? 'flipped' : ''}`}><path fillRule="evenodd" d="M13.78 10.47a.75.75 0 0 1 0 1.06l-2.25 2.25a.75.75 0 0 1-1.06 0l-2.25-2.25a.75.75 0 1 1 1.06-1.06l.97.97V5.75a.75.75 0 0 1 1.5 0v5.69l.97-.97a.75.75 0 0 1 1.06 0ZM2.22 5.53a.75.75 0 0 1 0-1.06l2.25-2.25a.75.75 0 0 1 1.06 0l2.25 2.25a.75.75 0 0 1-1.06 1.06l-.97-.97v5.69a.75.75 0 0 1-1.5 0V4.56l-.97.97a.75.75 0 0 1-1.06 0Z" clipRule="evenodd" /></svg>
                            </button>
                        </div>
                        {hostTab === 'Hostname' && (() => {
                            const list = [...hostnamesData].sort((a, b) => (hostMetric === 'Revenue' ? b.revenue - a.revenue : b.visitors - a.visitors));
                            const maxVal = hostMetric === 'Revenue' ? maxHostnameRevenue : maxHostname;
                            const getVal = (x) => (hostMetric === 'Revenue' ? x.revenue : x.visitors);
                            return list.slice(0, CARD_LIST_LIMIT).map((h, i) => (
                                <div key={i} className="dashboard-tech-row dashboard-acquisition-row-hover">
                                    <div className="dashboard-list-label">{h.name}</div>
                                    <div className="dashboard-list-value">{hostMetric === 'Revenue' ? `$${h.revenue}` : h.visitors}</div>
                                    <div className="dashboard-list-bar">
                                        <div className="dashboard-list-bar-fill" style={{ width: `${maxVal ? (getVal(h) / maxVal) * 100 : 0}%` }} />
                                    </div>
                                    <div className="dashboard-tech-detail-popover" role="tooltip">
                                        <div className="dashboard-tech-popover-header">{h.name}</div>
                                        <div className="dashboard-tech-popover-row">
                                            <span className="dashboard-tech-popover-swatch visitors" />
                                            <span>Visitors</span>
                                            <strong>{h.visitors}</strong>
                                        </div>
                                        <div className="dashboard-tech-popover-row">
                                            <span className="dashboard-tech-popover-swatch revenue" />
                                            <span>Revenue</span>
                                            <strong>${h.revenue}</strong>
                                        </div>
                                        <div className="dashboard-tech-popover-row">
                                            <span>Revenue/visitor</span>
                                            <strong>${h.revenuePerVisitor.toFixed(2)}</strong>
                                        </div>
                                        <div className="dashboard-tech-popover-row">
                                            <span>Conversion rate</span>
                                            <strong>{h.conversionRate}%</strong>
                                        </div>
                                    </div>
                                </div>
                            ));
                        })()}
                        {hostTab === 'Page' && (() => {
                            const list = [...pagesData].sort((a, b) => (hostMetric === 'Revenue' ? b.revenue - a.revenue : b.visitors - a.visitors));
                            const maxVal = hostMetric === 'Revenue' ? maxPageRevenue : maxPage;
                            const getVal = (x) => (hostMetric === 'Revenue' ? x.revenue : x.visitors);
                            return list.slice(0, CARD_LIST_LIMIT).map((p, i) => (
                                <div key={i} className="dashboard-tech-row dashboard-acquisition-row-hover">
                                    <div className="dashboard-list-label">{p.name}</div>
                                    <div className="dashboard-list-value">{hostMetric === 'Revenue' ? `$${p.revenue}` : p.visitors}</div>
                                    <div className="dashboard-list-bar">
                                        <div className="dashboard-list-bar-fill" style={{ width: `${maxVal ? (getVal(p) / maxVal) * 100 : 0}%` }} />
                                    </div>
                                    <div className="dashboard-tech-detail-popover" role="tooltip">
                                        <div className="dashboard-tech-popover-header">{p.name}</div>
                                        <div className="dashboard-tech-popover-row">
                                            <span className="dashboard-tech-popover-swatch visitors" />
                                            <span>Visitors</span>
                                            <strong>{p.visitors}</strong>
                                        </div>
                                        <div className="dashboard-tech-popover-row">
                                            <span className="dashboard-tech-popover-swatch revenue" />
                                            <span>Revenue</span>
                                            <strong>${p.revenue}</strong>
                                        </div>
                                        <div className="dashboard-tech-popover-row">
                                            <span>Revenue/visitor</span>
                                            <strong>${p.revenuePerVisitor.toFixed(2)}</strong>
                                        </div>
                                        <div className="dashboard-tech-popover-row">
                                            <span>Conversion rate</span>
                                            <strong>{p.conversionRate}%</strong>
                                        </div>
                                    </div>
                                </div>
                            ));
                        })()}
                        {hostTab === 'Entry page' && (() => {
                            const list = [...entryPagesData].sort((a, b) => (hostMetric === 'Revenue' ? b.revenue - a.revenue : b.visitors - a.visitors));
                            const maxVal = hostMetric === 'Revenue' ? maxEntryPageRevenue : maxEntryPage;
                            const getVal = (x) => (hostMetric === 'Revenue' ? x.revenue : x.visitors);
                            return list.slice(0, CARD_LIST_LIMIT).map((p, i) => (
                                <div key={i} className="dashboard-tech-row dashboard-acquisition-row-hover">
                                    <div className="dashboard-list-label">{p.name}</div>
                                    <div className="dashboard-list-value">{hostMetric === 'Revenue' ? `$${p.revenue}` : p.visitors}</div>
                                    <div className="dashboard-list-bar">
                                        <div className="dashboard-list-bar-fill" style={{ width: `${maxVal ? (getVal(p) / maxVal) * 100 : 0}%` }} />
                                    </div>
                                    <div className="dashboard-tech-detail-popover" role="tooltip">
                                        <div className="dashboard-tech-popover-header">{p.name}</div>
                                        <div className="dashboard-tech-popover-row">
                                            <span className="dashboard-tech-popover-swatch visitors" />
                                            <span>Visitors</span>
                                            <strong>{p.visitors}</strong>
                                        </div>
                                        <div className="dashboard-tech-popover-row">
                                            <span className="dashboard-tech-popover-swatch revenue" />
                                            <span>Revenue</span>
                                            <strong>${p.revenue}</strong>
                                        </div>
                                        <div className="dashboard-tech-popover-row">
                                            <span>Revenue/visitor</span>
                                            <strong>${p.revenuePerVisitor.toFixed(2)}</strong>
                                        </div>
                                        <div className="dashboard-tech-popover-row">
                                            <span>Conversion rate</span>
                                            <strong>{p.conversionRate}%</strong>
                                        </div>
                                    </div>
                                </div>
                            ));
                        })()}
                        {hostTab === 'Exit link' && (() => {
                            const list = [...exitLinksData].sort((a, b) => (hostMetric === 'Revenue' ? b.revenue - a.revenue : b.visitors - a.visitors));
                            const maxVal = hostMetric === 'Revenue' ? maxExitLinkRevenue : maxExitLink;
                            const getVal = (x) => (hostMetric === 'Revenue' ? x.revenue : x.visitors);
                            return list.slice(0, CARD_LIST_LIMIT).map((e, i) => (
                                <div key={i} className="dashboard-tech-row dashboard-acquisition-row-hover">
                                    <div className="dashboard-list-label">
                                        {e.icon && <span className="dashboard-tech-icon dashboard-exit-link-icon" aria-hidden>{e.icon}</span>}
                                        {e.name}
                                    </div>
                                    <div className="dashboard-list-value">{hostMetric === 'Revenue' ? `$${e.revenue}` : e.visitors}</div>
                                    <div className="dashboard-list-bar">
                                        <div className="dashboard-list-bar-fill" style={{ width: `${maxVal ? (getVal(e) / maxVal) * 100 : 0}%` }} />
                                    </div>
                                    <div className="dashboard-tech-detail-popover" role="tooltip">
                                        <div className="dashboard-tech-popover-header">
                                            {e.icon && <span className="dashboard-tech-icon" aria-hidden>{e.icon}</span>}
                                            {e.name}
                                        </div>
                                        <div className="dashboard-tech-popover-row">
                                            <span className="dashboard-tech-popover-swatch visitors" />
                                            <span>Visitors</span>
                                            <strong>{e.visitors}</strong>
                                        </div>
                                        <div className="dashboard-tech-popover-row">
                                            <span className="dashboard-tech-popover-swatch revenue" />
                                            <span>Revenue</span>
                                            <strong>${e.revenue}</strong>
                                        </div>
                                        <div className="dashboard-tech-popover-row">
                                            <span>Revenue/visitor</span>
                                            <strong>${e.revenuePerVisitor.toFixed(2)}</strong>
                                        </div>
                                        <div className="dashboard-tech-popover-row">
                                            <span>Conversion rate</span>
                                            <strong>{e.conversionRate}%</strong>
                                        </div>
                                    </div>
                                </div>
                            ));
                        })()}
                        <div className="dashboard-details-btn-wrap">
                            <button
                                type="button"
                                className="dashboard-details-btn"
                                onClick={() => setHostDetailModalOpen(true)}
                            >
                                <DetailsExpandIcon />
                                DETAILS
                            </button>
                        </div>
                    </div>

                    <div className="dashboard-section-card dashboard-tech-card" aria-label="Browser, OS, and device">
                        <div className="dashboard-tech-header">
                            <div className="dashboard-section-tabs">
                                {['Browser', 'OS', 'Device'].map(t => (
                                    <button key={t} type="button" className={techTab === t ? 'active' : ''} onClick={() => setTechTab(t)}>{t}</button>
                                ))}
                            </div>
                            <button
                                type="button"
                                className="dashboard-metric-filter active"
                                onClick={() => setTechMetric(m => m === 'Visitors' ? 'Revenue' : 'Visitors')}
                                aria-pressed={techMetric === 'Visitors'}
                                aria-label={techMetric === 'Visitors' ? 'Sort by Visitors; click to sort by Revenue' : 'Sort by Revenue; click to sort by Visitors'}
                            >
                                {techMetric === 'Visitors' ? 'Visitors' : 'Revenue'}
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className={`dashboard-metric-icon ${techMetric === 'Revenue' ? 'flipped' : ''}`}><path fillRule="evenodd" d="M13.78 10.47a.75.75 0 0 1 0 1.06l-2.25 2.25a.75.75 0 0 1-1.06 0l-2.25-2.25a.75.75 0 1 1 1.06-1.06l.97.97V5.75a.75.75 0 0 1 1.5 0v5.69l.97-.97a.75.75 0 0 1 1.06 0ZM2.22 5.53a.75.75 0 0 1 0-1.06l2.25-2.25a.75.75 0 0 1 1.06 0l2.25 2.25a.75.75 0 0 1-1.06 1.06l-.97-.97v5.69a.75.75 0 0 1-1.5 0V4.56l-.97.97a.75.75 0 0 1-1.06 0Z" clipRule="evenodd" /></svg>
                            </button>
                        </div>
                        {techTab === 'Browser' && (() => {
                            const list = [...browsersData].sort((a, b) => (techMetric === 'Revenue' ? b.revenue - a.revenue : b.visitors - a.visitors));
                            const maxVal = techMetric === 'Revenue' ? maxBrowserRevenue : maxBrowser;
                            const getVal = (x) => techMetric === 'Revenue' ? x.revenue : x.visitors;
                            return list.slice(0, CARD_LIST_LIMIT).map((b, i) => (
                                <div key={i} className="dashboard-tech-row dashboard-acquisition-row-hover">
                                    <div className="dashboard-list-label">
                                        <span className="dashboard-tech-icon" aria-hidden>{b.icon}</span>
                                        {b.name}
                                    </div>
                                    <div className="dashboard-list-value">{techMetric === 'Revenue' ? `$${b.revenue}` : b.visitors}</div>
                                    <div className="dashboard-list-bar">
                                        <div className="dashboard-list-bar-fill" style={{width: `${maxVal ? (getVal(b) / maxVal) * 100 : 0}%`}} />
                                    </div>
                                    <div className="dashboard-tech-detail-popover" role="tooltip">
                                        <div className="dashboard-tech-popover-header">
                                            <span className="dashboard-tech-icon" aria-hidden>{b.icon}</span>
                                            {b.name}
                                        </div>
                                        <div className="dashboard-tech-popover-row">
                                            <span className="dashboard-tech-popover-swatch visitors" />
                                            <span>Visitors</span>
                                            <strong>{b.visitors}</strong>
                                        </div>
                                        <div className="dashboard-tech-popover-row">
                                            <span className="dashboard-tech-popover-swatch revenue" />
                                            <span>Revenue</span>
                                            <strong>${b.revenue}</strong>
                                        </div>
                                        <div className="dashboard-tech-popover-row">
                                            <span>Revenue/visitor</span>
                                            <strong>${b.revenuePerVisitor.toFixed(2)}</strong>
                                        </div>
                                        <div className="dashboard-tech-popover-row">
                                            <span>Conversion rate</span>
                                            <strong>{b.conversionRate}%</strong>
                                        </div>
                                    </div>
                                </div>
                            ));
                        })()}
                        {techTab === 'OS' && (() => {
                            const list = [...osListData].sort((a, b) => (techMetric === 'Revenue' ? b.revenue - a.revenue : b.visitors - a.visitors));
                            const maxVal = techMetric === 'Revenue' ? maxOSRevenue : maxOS;
                            const getVal = (x) => techMetric === 'Revenue' ? x.revenue : x.visitors;
                            return list.slice(0, CARD_LIST_LIMIT).map((o, i) => (
                                <div key={i} className="dashboard-tech-row dashboard-acquisition-row-hover">
                                    <div className="dashboard-list-label">
                                        <span className="dashboard-tech-icon" aria-hidden>{o.icon}</span>
                                        {o.name}
                                    </div>
                                    <div className="dashboard-list-value">{techMetric === 'Revenue' ? `$${o.revenue}` : o.visitors}</div>
                                    <div className="dashboard-list-bar">
                                        <div className="dashboard-list-bar-fill" style={{width: `${maxVal ? (getVal(o) / maxVal) * 100 : 0}%`}} />
                                    </div>
                                    <div className="dashboard-tech-detail-popover" role="tooltip">
                                        <div className="dashboard-tech-popover-header">
                                            <span className="dashboard-tech-icon" aria-hidden>{o.icon}</span>
                                            {o.name}
                                        </div>
                                        <div className="dashboard-tech-popover-row">
                                            <span className="dashboard-tech-popover-swatch visitors" />
                                            <span>Visitors</span>
                                            <strong>{o.visitors}</strong>
                                        </div>
                                        <div className="dashboard-tech-popover-row">
                                            <span className="dashboard-tech-popover-swatch revenue" />
                                            <span>Revenue</span>
                                            <strong>${o.revenue}</strong>
                                        </div>
                                        <div className="dashboard-tech-popover-row">
                                            <span>Revenue/visitor</span>
                                            <strong>${o.revenuePerVisitor.toFixed(2)}</strong>
                                        </div>
                                        <div className="dashboard-tech-popover-row">
                                            <span>Conversion rate</span>
                                            <strong>{o.conversionRate}%</strong>
                                        </div>
                                    </div>
                                </div>
                            ));
                        })()}
                        {techTab === 'Device' && (() => {
                            const list = [...devicesData].sort((a, b) => (techMetric === 'Revenue' ? b.revenue - a.revenue : b.visitors - a.visitors));
                            const maxVal = techMetric === 'Revenue' ? maxDeviceRevenue : maxDevice;
                            const getVal = (x) => techMetric === 'Revenue' ? x.revenue : x.visitors;
                            return list.slice(0, CARD_LIST_LIMIT).map((d, i) => (
                                <div key={i} className="dashboard-tech-row dashboard-acquisition-row-hover">
                                    <div className="dashboard-list-label">
                                        <span className="dashboard-tech-icon" aria-hidden>{d.icon}</span>
                                        {d.name}
                                    </div>
                                    <div className="dashboard-list-value">{techMetric === 'Revenue' ? `$${d.revenue}` : d.visitors}</div>
                                    <div className="dashboard-list-bar">
                                        <div className="dashboard-list-bar-fill" style={{width: `${maxVal ? (getVal(d) / maxVal) * 100 : 0}%`}} />
                                    </div>
                                    <div className="dashboard-tech-detail-popover" role="tooltip">
                                        <div className="dashboard-tech-popover-header">
                                            <span className="dashboard-tech-icon" aria-hidden>{d.icon}</span>
                                            {d.name}
                                        </div>
                                        <div className="dashboard-tech-popover-row">
                                            <span className="dashboard-tech-popover-swatch visitors" />
                                            <span>Visitors</span>
                                            <strong>{d.visitors}</strong>
                                        </div>
                                        <div className="dashboard-tech-popover-row">
                                            <span className="dashboard-tech-popover-swatch revenue" />
                                            <span>Revenue</span>
                                            <strong>${d.revenue}</strong>
                                        </div>
                                        <div className="dashboard-tech-popover-row">
                                            <span>Revenue/visitor</span>
                                            <strong>${d.revenuePerVisitor.toFixed(2)}</strong>
                                        </div>
                                        <div className="dashboard-tech-popover-row">
                                            <span>Conversion rate</span>
                                            <strong>{d.conversionRate}%</strong>
                                        </div>
                                    </div>
                                </div>
                            ));
                        })()}
                        <div className="dashboard-details-btn-wrap">
                            <button
                                type="button"
                                className="dashboard-details-btn"
                                onClick={() => setTechDetailModalOpen(true)}
                            >
                                <DetailsExpandIcon />
                                DETAILS
                            </button>
                        </div>
                </div>
                        </div>
                    </section>

                <div className="dashboard-section-card dashboard-journey-card" style={{ gridColumn: '1 / -1' }}>
                    <div className="dashboard-journey-header">
                        <div className="dashboard-section-tabs dashboard-journey-tabs">
                            {['Goal', 'Funnel', 'User', 'Journey for payment'].map((t, idx) => (
                                <button
                                    key={t}
                                    type="button"
                                    className={`${journeyTab === t ? 'active' : ''} ${idx < 2 ? 'dashboard-tab-hover' : ''}`}
                                    onClick={() => setJourneyTab(t)}
                                >
                                    {t}
                                </button>
                            ))}
                        </div>
                        {journeyTab === 'Goal' && (
                            <button type="button" className="dashboard-add-goals-btn">+ Add goals</button>
                        )}
                        {journeyTab === 'Funnel' && (
                            <div className="dashboard-funnel-conversion-badge">
                                <div className="dashboard-funnel-conversion-value">4.2% conversion rate</div>
                                <div className="dashboard-funnel-conversion-label">Today</div>
                            </div>
                        )}
                    </div>

                    {journeyTab === 'Goal' && (
                        <>
                            <div className="dashboard-goal-list">
                                {(journeyData?.goals ?? []).slice(0, CARD_LIST_LIMIT).map((g, i) => (
                                    <div
                                        key={i}
                                        className="dashboard-goal-row"
                                        onMouseMove={(e) => handleRowMouseMove(e, { type: 'goal', ...g })}
                                        onMouseLeave={handleRowMouseLeave}
                                    >
                                        <div className="dashboard-goal-name">{g.name}</div>
                                        <div className="dashboard-goal-bar-wrap">
                                            <div
                                                className="dashboard-goal-bar-fill"
                                                style={{ width: `${(g.count / ((journeyData?.goals ?? [])[0]?.count || 1)) * 100}%` }}
                                            />
                                        </div>
                                        <div className="dashboard-goal-stats">
                                            <span className="dashboard-goal-count">{g.count}</span>
                                            <span className="dashboard-goal-pct">({g.pct.toFixed(2)}%)</span>
                                        </div>
                                        <div className="dashboard-goal-actions">
                                            <button type="button" className="dashboard-goal-action-btn" title="View goal details">
                                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" width="18" height="18">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.64 0 8.577 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.64 0-8.577-3.007-9.963-7.178Z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                                                </svg>
                                            </button>
                                            <button type="button" className="dashboard-goal-action-btn" title="Filter by this goal" onClick={() => addFilter('Goal', g.name)}>
                                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" width="18" height="18">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 0 1-.659 1.591l-5.432 5.432a2.25 2.25 0 0 0-.659 1.591v2.927a2.25 2.25 0 0 1-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 0 0-.659-1.591L3.659 7.409A2.25 2.25 0 0 1 3 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0 1 12 3Z" />
                                                </svg>
                                            </button>
                                        </div>
                                        {tooltipData?.type === 'goal' && tooltipData?.name === g.name && (
                                            <div
                                                className="dashboard-goal-hover-tooltip"
                                                role="tooltip"
style={{
                                                    left: tooltipPos.x + (tooltipPos.side === 'left' ? -20 : 20),
                                                    top: tooltipPos.y + (tooltipPos.vertical === 'top' ? -20 : 20),
                                                    transform: `translate(${tooltipPos.side === 'left' ? '-100%' : '0%'}, ${tooltipPos.vertical === 'top' ? '-100%' : '0%'})`
                                                }}
                                            >
                                                <div className="dashboard-goal-hover-tooltip-header">
                                                    <span className="dashboard-goal-hover-tooltip-icon">◎</span>
                                                    {g.name}
                                                </div>
                                                <p className="dashboard-goal-hover-tooltip-text">
                                                    <strong>{g.count}</strong> unique visitors completed this goal. That&apos;s <strong>{g.pct.toFixed(2)}%</strong> of the <strong>{journeyData?.goalsTotalVisitors ?? 0}</strong> unique visitors during this period.
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                            <div className="dashboard-details-btn-wrap">
                                <button
                                    type="button"
                                    className="dashboard-details-btn"
                                    onClick={() => setGoalDetailModalOpen(true)}
                                >
                                    <DetailsExpandIcon />
                                    DETAILS
                                </button>
                            </div>
                        </>
                    )}

                    {journeyTab === 'Funnel' && (
                        <div className="dashboard-funnel-viz">
                            <div className="dashboard-funnel-flow">
                                {(journeyData?.funnelStages ?? []).map((stage, i) => (
                                    <React.Fragment key={i}>
                                        <div className="dashboard-funnel-segment" style={{ flex: stage.visitors }}>
                                            <div className="dashboard-funnel-segment-inner" />
                                        </div>
                                        {i < (journeyData?.funnelStages ?? []).length - 1 && stage.dropOffPct != null && (
                                            <div className="dashboard-funnel-dropoff">-{stage.dropOffPct}% →</div>
                                        )}
                                    </React.Fragment>
                                ))}
                            </div>
                            <div className="dashboard-funnel-labels">
                                {(journeyData?.funnelStages ?? []).map((stage, i) => (
                                    <div key={i} className="dashboard-funnel-label-cell">
                                        <div className="dashboard-funnel-label-visitors">{stage.visitors} visitors</div>
                                        <div className="dashboard-funnel-label-name">
                                            <span className="dashboard-funnel-stage-icon" aria-hidden>{stage.icon}</span>
                                            {stage.label}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {journeyTab === 'User' && (
                        <>
                            <input
                                type="search"
                                className="dashboard-journey-search"
                                placeholder="Search"
                                value={userSearch}
                                onChange={e => setUserSearch(e.target.value)}
                                aria-label="Search visitors"
                            />
                            <div className="dashboard-user-list">
                                <div className="dashboard-user-list-header">
                                    <span>Visitor</span>
                                    <span>Source</span>
                                    <span>Spent</span>
                                    <span>Last seen</span>
                                </div>
                                {visitorsData.filter(u => !userSearch.trim() || u.alias.toLowerCase().includes(userSearch.trim().toLowerCase()))
                                    .map((u, i) => (
                                        <div key={i} className="dashboard-user-row">
                                            <div className="dashboard-user-visitor">
                                                <div className="dashboard-journey-avatar dashboard-user-avatar" />
                                                <div className="dashboard-user-meta">
                                                    <div className="dashboard-user-alias">{u.alias}</div>
                                                    <div className="dashboard-user-details">
                                                        <span><img src={getCountryFlagUrl(u.countryCode)} alt={u.country} className="dashboard-geo-flag-img" /> {u.country}</span>
                                                        <span>{u.device}</span>
                                                        <span>{u.os}</span>
                                                        <span>{u.browser}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="dashboard-user-source">{u.source}</div>
                                            <div className="dashboard-user-spent">{u.spent}</div>
                                            <div className="dashboard-user-lastseen">
                                                <div>{u.lastSeen}</div>
                                                <div className="dashboard-user-activity-dots" aria-hidden>
                                                    {[1, 2, 3, 4, 5].map(d => (
                                                        <span key={d} className={d <= u.activityDots ? 'filled' : ''} />
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        </>
                    )}

                    {journeyTab === 'Journey for payment' && (
                        <>
                            <input
                                type="search"
                                className="dashboard-journey-search"
                                placeholder="Search"
                                value={journeySearch}
                                onChange={e => setJourneySearch(e.target.value)}
                                aria-label="Search"
                            />
                            <div className="dashboard-journey-table-wrap">
                                <table className="dashboard-journey-payment-table">
                                    <thead>
                                        <tr>
                                            <th>Visitor</th>
                                            <th>Source</th>
                                            <th>Spent</th>
                                            <th>Time to complete</th>
                                            <th>Completed at</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(journeyData?.journeyPayment ?? []).filter(j => !journeySearch.trim() || (j.visitor || '').toLowerCase().includes(journeySearch.trim().toLowerCase()) || (j.country || '').toLowerCase().includes(journeySearch.trim().toLowerCase()) || (j.source || '').toLowerCase().includes(journeySearch.trim().toLowerCase()))
                                            .map((j, i) => (
                                                <tr key={i}>
                                                    <td>
                                                        <div className="dashboard-journey-payment-visitor">
                                                            <div className="dashboard-journey-payment-name">{j.visitor} <span className="dashboard-journey-payment-tag">{j.tag}</span></div>
                                                            <div className="dashboard-journey-payment-meta">
                                                                <img src={`${FLAG_BASE}/${j.countryCode}.svg`} alt={j.country} className="dashboard-journey-payment-flag" />
                                                                <span>{j.country}</span>
                                                                <img src={`https://datafa.st/images/devices/${j.device}.png`} alt="" className="dashboard-journey-payment-device" onError={e => { e.target.style.display = 'none'; }} />
                                                                <span>{j.device}</span>
                                                                <img src={`https://datafa.st/images/oss/${j.os === 'Mac OS' ? 'MAC' : j.os === 'Windows' ? 'WIN' : 'MAC'}.png`} alt="" className="dashboard-journey-payment-os" onError={e => { e.target.style.display = 'none'; }} />
                                                                <span>{j.os}</span>
                                                                <img src={`https://cdnjs.cloudflare.com/ajax/libs/browser-logos/74.1.0/${({ Chrome: 'chrome', Safari: 'safari', Edge: 'edge' })[j.browser] || 'chrome'}/${({ Chrome: 'chrome', Safari: 'safari', Edge: 'edge' })[j.browser] || 'chrome'}_64x64.png`} alt="" className="dashboard-journey-payment-browser" onError={e => { e.target.style.display = 'none'; }} />
                                                                <span>{j.browser}</span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="dashboard-journey-payment-source">
                                                            {j.sourceIcon ? <img src={j.sourceIcon} alt="" className="dashboard-journey-payment-source-icon" /> : null}
                                                            <span>{j.source}</span>
                                                        </div>
                                                    </td>
                                                    <td className="dashboard-journey-payment-spent">{j.spent}</td>
                                                    <td>{j.timeToComplete}</td>
                                                    <td>{j.completedAt}</td>
                                                </tr>
                                            ))}
                                    </tbody>
                                </table>
                            </div>
                            <button type="button" className="dashboard-load-more-btn">Load more (62)</button>
                        </>
                    )}
                </div>

                {goalDetailModalOpen && (
                    <div className="dashboard-modal-overlay" onClick={() => setGoalDetailModalOpen(false)} role="dialog" aria-modal="true" aria-labelledby="goal-detail-title">
                        <div className="dashboard-modal dashboard-channel-detail-modal" onClick={e => e.stopPropagation()}>
                            <div className="dashboard-modal-header">
                                <h2 id="goal-detail-title" className="dashboard-modal-title">Goal</h2>
                                <input
                                    type="search"
                                    className="dashboard-modal-search"
                                    placeholder="Search..."
                                    value={goalDetailSearch}
                                    onChange={e => setGoalDetailSearch(e.target.value)}
                                    aria-label="Search goals"
                                />
                                <button
                                    type="button"
                                    className="dashboard-modal-close"
                                    onClick={() => setGoalDetailModalOpen(false)}
                                    aria-label="Close"
                                >
                                    ×
                                </button>
                            </div>
                            <div className="dashboard-modal-body">
                                <table className="dashboard-detail-table">
                                    <thead>
                                        <tr>
                                            <th>Goal</th>
                                            <th>Count</th>
                                            <th>Percentage</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(journeyData?.goals ?? []).filter(g => !goalDetailSearch.trim() || g.name.toLowerCase().includes(goalDetailSearch.trim().toLowerCase()))
                                            .map((g, i) => (
                                                <tr key={i}>
                                                    <td>{g.name}</td>
                                                    <td>{g.count}</td>
                                                    <td>{g.pct.toFixed(2)}%</td>
                                                </tr>
                                            ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {/* Bottom Fixed Action Buttons */}
                <div className="dashboard-bottom-actions">
                    <button
                        type="button"
                        className="dashboard-bottom-action-btn"
                        onClick={() => { setGlobeLoading(true); setGlobeModalOpen(true); }}
                        title="Real-time Globe View"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10"/>
                            <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/>
                            <path d="M2 12h20"/>
                        </svg>
                        <span>Globe</span>
                    </button>
                    <button
                        type="button"
                        className="dashboard-bottom-action-btn"
                        title="Map View"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="m3 7 6-3 6 3 6-3v13l-6 3-6-3-6 3V7z"/>
                            <path d="M9 4v13"/>
                            <path d="M15 7v13"/>
                        </svg>
                        <span>Map</span>
                    </button>
                </div>

                {/* Globe Modal - Fullscreen: portal to document.body so it renders above sidebar */}
                {globeModalOpen && createPortal(
                    <div
                        className="dashboard-globe-modal-overlay"
                        style={{ position: 'fixed', inset: 0, zIndex: 2147483647 }}
                    >
                        {/* Space Background Layers — transforms driven by JS (rAF + lerp) */}
                        <div className="globe-space-bg">
                            <div className="globe-space-nebula"/>
                            <div className="globe-stars-far"/>
                            <div className="globe-stars-mid"/>
                            <div className="globe-stars-near"/>
                            <div className="globe-shooting-star"/>
                            <div className="globe-shooting-star-2"/>
                            <div className="globe-shooting-star-3"/>
                        </div>
                        <div className="dashboard-globe-modal">
                            {/* Close Button */}
                            <button
                                type="button"
                                className="dashboard-globe-close-btn"
                                onClick={() => setGlobeModalOpen(false)}
                                aria-label="Close"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M18 6 6 18"/>
                                    <path d="m6 6 12 12"/>
                                </svg>
                            </button>

                            {/* Left Panel - Stats & Activity */}
                            <div className="dashboard-globe-panel dashboard-globe-panel-top">
                                <div className="dashboard-globe-panel-header">
                                    <div className="dashboard-globe-logo">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                                        </svg>
                                        <span className="dashboard-globe-logo-text">Optimus</span>
                                    </div>
                                    <span className="dashboard-globe-divider">|</span>
                                    <span className="dashboard-globe-realtime-badge">REAL-TIME</span>
                                    <div className="dashboard-globe-actions">
                                        <button
                                            type="button"
                                            className={`dashboard-globe-action-btn ${isAutoPanning ? 'active' : ''}`}
                                            onClick={() => setIsAutoPanning(!isAutoPanning)}
                                            title={isAutoPanning ? 'Stop auto-panning' : 'Start auto-panning'}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
                                                <path d="M3 3v5h5"/>
                                            </svg>
                                        </button>
                                        <button
                                            type="button"
                                            className="dashboard-globe-action-btn"
                                            title="Fullscreen"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M8 3H5a2 2 0 0 0-2 2v3"/>
                                                <path d="M21 8V5a2 2 0 0 0-2-2h-3"/>
                                                <path d="M3 16v3a2 2 0 0 0 2 2h3"/>
                                                <path d="M16 21h3a2 2 0 0 0 2-2v-3"/>
                                            </svg>
                                        </button>
                                    </div>
                                </div>

                                <div className="dashboard-globe-visitors-count">
                                    <span className="dashboard-globe-online-dot">
                                        <span className="dashboard-globe-online-ping"/>
                                        <span className="dashboard-globe-online-solid"/>
                                    </span>
                                    <span className="dashboard-globe-count">{realtimeVisitorsData.length + (myLocation ? 1 : 0)}</span>
                                    <span className="dashboard-globe-count-label">{myLocation ? '(incl. you) on' : 'visitors on'}</span>
                                    <img src="/images/optimus-icon.png" alt="" className="dashboard-globe-site-icon"/>
                                    <a href="#" className="dashboard-globe-site-link">Optimus</a>
                                   <span className="dashboard-globe-value">(est. value: <strong>$2</strong>)</span>
                                </div>

                                <div className="dashboard-globe-stats">
                                    {realtimeVisitorsData.length === 0 ? (
                                        <div style={{ padding: '12px 0', color: 'var(--globe-text-muted)', fontSize: '13px', textAlign: 'center' }}>
                                            No active visitors right now
                                        </div>
                                    ) : (
                                        <>
                                            <div className="dashboard-globe-stat-row">
                                                <span className="dashboard-globe-stat-label">Referrers</span>
                                                <div className="dashboard-globe-stat-tags">
                                                    {[...new Set(realtimeVisitorsData.map(v => v.referrer).filter(Boolean))].map((ref, i) => (
                                                        <span key={i} className="dashboard-globe-stat-tag">
                                                            {ref !== 'Direct' && (
                                                                <img src={`https://icons.duckduckgo.com/ip3/${ref.replace(/^https?:\/\//, '').split('/')[0]}.ico`} alt="" className="dashboard-globe-tag-icon" onError={(e) => {e.target.style.display='none';}}/>
                                                            )}
                                                            {ref} ({realtimeVisitorsData.filter(v => v.referrer === ref).length})
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                            <div className="dashboard-globe-stat-row">
                                                <span className="dashboard-globe-stat-label">Countries</span>
                                                <div className="dashboard-globe-stat-tags">
                                                    {[...new Set(realtimeVisitorsData.map(v => v.country).filter(Boolean))].map((country, i) => {
                                                        const visitor = realtimeVisitorsData.find(v => v.country === country);
                                                        return (
                                                            <span key={i} className="dashboard-globe-stat-tag">
                                                                {visitor?.countryCode && (
                                                                    <img src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${visitor.countryCode}.svg`} alt="" className="dashboard-globe-flag-icon" onError={(e) => {e.target.style.display='none';}}/>
                                                                )}
                                                                {country} ({realtimeVisitorsData.filter(v => v.country === country).length})
                                                            </span>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                            <div className="dashboard-globe-stat-row">
                                                <span className="dashboard-globe-stat-label">Devices</span>
                                                <div className="dashboard-globe-stat-tags">
                                                    {[...new Set(realtimeVisitorsData.map(v => v.device).filter(Boolean))].map((device, i) => (
                                                        <span key={i} className="dashboard-globe-stat-tag">
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                {device === 'desktop' && <><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8"/><path d="M12 17v4"/></>}
                                                                {device === 'mobile' && <><rect x="5" y="2" width="14" height="20" rx="2"/><path d="M12 18h.01"/></>}
                                                                {device === 'tablet' && <><rect x="4" y="2" width="16" height="20" rx="2"/><path d="M12 18h.01"/></>}
                                                            </svg>
                                                            {device} ({realtimeVisitorsData.filter(v => v.device === device).length})
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Activity Feed Panel */}
                            <div className="dashboard-globe-panel dashboard-globe-panel-bottom">
                                <div className="dashboard-globe-activity-header">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                                    </svg>
                                    <span>Live Activity</span>
                                    <span className="dashboard-globe-activity-count">{realtimeActivityData.length}</span>
                                </div>
                                <div className="dashboard-globe-activity-feed">
                                    {realtimeActivityData.length === 0 ? (
                                        <div style={{ padding: '16px', color: 'var(--globe-text-faint)', fontSize: '13px', textAlign: 'center' }}>
                                            No recent activity
                                        </div>
                                    ) : (
                                        realtimeActivityData.map((activity, i) => (
                                            <div key={i} className="dashboard-globe-activity-item">
                                                <div className="dashboard-globe-activity-icon">
                                                    {activity.type === 'pageview' ? (
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"/>
                                                            <circle cx="12" cy="12" r="3"/>
                                                        </svg>
                                                    ) : (
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <circle cx="12" cy="12" r="10"/>
                                                            <circle cx="12" cy="12" r="6"/>
                                                            <circle cx="12" cy="12" r="2"/>
                                                        </svg>
                                                    )}
                                                </div>
                                                <div className="dashboard-globe-activity-content">
                                                    <span className="dashboard-globe-activity-name">{activity.visitorName}</span>
                                                    {activity.type === 'pageview' ? (
                                                        <>
                                                            <span> from </span>
                                                            <span className="dashboard-globe-activity-country">
                                                                {activity.countryCode && (
                                                                    <img src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${activity.countryCode}.svg`} alt="" className="dashboard-globe-activity-flag" onError={(e) => {e.target.style.display='none';}}/>
                                                                )}
                                                                {activity.country || 'Unknown'}
                                                            </span>
                                                            <span> visited </span>
                                                            <span className="dashboard-globe-activity-page">{activity.page || '/'}</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <span> performed </span>
                                                            <span className="dashboard-globe-activity-event">{activity.action}</span>
                                                        </>
                                                    )}
                                                    <div className="dashboard-globe-activity-time">{activity.time}</div>
                                                </div>
                                                <span className="dashboard-globe-activity-dot"/>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>

                            {/* Globe Loading Overlay */}
                            {globeLoading && (
                                <div className="dashboard-globe-loading-overlay">
                                    <div className="dashboard-globe-loading-content">
                                        <div className="dashboard-globe-loading-text">Initializing map...</div>
                                        <div className="dashboard-globe-loading-bar">
                                            <div className="dashboard-globe-loading-progress"></div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Globe Map */}
                            <div className="dashboard-globe-map-container">
                                <Globe
                                    ref={globeMapRef}
                                    globeImageUrl="https://unpkg.com/three-globe@2.31.1/example/img/earth-day.jpg"
                                    backgroundColor="rgba(0,0,0,0)"
                                    backgroundImageUrl=""
                                    showGlobe={true}
                                    showAtmosphere={true}
                                    atmosphereColor="#4a8eff"
                                    atmosphereAltitude={0.22}
                                    showGraticules={false}
                                    animateIn={false}
                                    arcsData={[]}
                                    pathsData={[]}
                                    ringsData={[]}
                                    pointsData={[]}
                                    labelsData={COUNTRY_LABELS}
                                    labelLat={d => d.lat}
                                    labelLng={d => d.lng}
                                    labelText={d => d.name}
                                    labelSize={d => d.name.includes('Ocean') ? 1.0 : 0.6}
                                    labelColor={() => 'rgba(255, 255, 255, 0.7)'}
                                    labelDotRadius={0}
                                    labelAltitude={0.01}
                                    labelsTransitionDuration={0}
                                    htmlElementsData={[
                                        // Auth user's own location pin
                                        ...(myLocation ? [{
                                            lat: myLocation.lat,
                                            lng: myLocation.lng,
                                            name: user?.name || 'You',
                                            avatar: user?.avatarUrl || '',
                                            country: 'You',
                                            countryCode: '__ME__',
                                            device: '—',
                                            referrer: '—',
                                            currentPage: 'Dashboard',
                                            lastSeen: 'Now',
                                            lastAction: 'Viewing dashboard',
                                            isMe: true,
                                        }] : []),
                                        // Realtime visitors
                                        ...realtimeVisitorsData.map(v => {
                                            const latest = visitorLatestActivity[v.name];
                                            return {
                                                lat: v.lat,
                                                lng: v.lng,
                                                name: v.name || 'Visitor',
                                                avatar: v.avatar,
                                                country: v.country || '—',
                                                countryCode: v.countryCode || 'US',
                                                device: v.device || '—',
                                                referrer: v.referrer || 'Direct',
                                                currentPage: latest?.page || '—',
                                                lastSeen: latest?.time || '—',
                                                lastAction: latest?.action || '—',
                                                isMe: false,
                                            };
                                        }),
                                    ]}
                                    htmlElement={d => {
                                        const esc = (s) => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
                                        const el = document.createElement('div');
                                        const tooltipCls = 'globe-marker-tooltip-card';

                                        if (d.isMe) {
                                            // Auth user – special "You" pin with pulsing ring & label
                                            el.className = 'globe-html-marker globe-html-marker--me';
                                            el.innerHTML = `
                                                <div class="globe-marker-wrapper globe-marker-wrapper--me">
                                                    <div class="${tooltipCls}">
                                                        <div class="globe-marker-tooltip-header">
                                                            <span class="globe-marker-tooltip-online" style="background:#10b981">You</span>
                                                            ${d.avatar ? `<img src="${esc(d.avatar)}" class="globe-marker-tooltip-avatar" alt="" />` : ''}
                                                            <div class="globe-marker-tooltip-title">${esc(d.name)}</div>
                                                        </div>
                                                        <div class="globe-marker-tooltip-rows">
                                                            <div class="globe-marker-tooltip-row"><span class="globe-marker-tooltip-label">Status</span><span class="globe-marker-tooltip-value">Viewing dashboard</span></div>
                                                        </div>
                                                    </div>
                                                    <div class="globe-marker-me-pin">
                                                        <div class="globe-marker-me-ping"></div>
                                                        <div class="globe-marker-me-dot"></div>
                                                    </div>
                                                    <span class="globe-marker-me-label">You</span>
                                                </div>
                                            `;
                                        } else {
                                            // Regular visitor pin
                                            el.className = 'globe-html-marker';
                                            const avatarHtml = d.avatar
                                                ? `<img src="${esc(d.avatar)}" class="globe-marker-tooltip-avatar" alt="" onerror="this.style.display='none'" />`
                                                : '';
                                            const flagHtml = d.countryCode && d.countryCode !== '__ME__'
                                                ? `<img src="https://purecatamphetamine.github.io/country-flag-icons/3x2/${esc(d.countryCode)}.svg" class="globe-marker-tooltip-flag" alt="" onerror="this.style.display='none'" />`
                                                : '';
                                            const markerAvatarHtml = d.avatar
                                                ? `<img src="${esc(d.avatar)}" class="globe-marker-avatar" alt="" onerror="this.style.display='none'" />`
                                                : '<div class="globe-marker-avatar" style="width:28px;height:28px;border-radius:50%;background:#475569;display:flex;align-items:center;justify-content:center;font-size:11px;color:#fff;border:2px solid #1e293b">' + esc(d.name?.[0] || 'V') + '</div>';
                                            const markerFlagHtml = d.countryCode && d.countryCode !== '__ME__'
                                                ? `<img src="https://purecatamphetamine.github.io/country-flag-icons/3x2/${esc(d.countryCode)}.svg" class="globe-marker-flag" alt="" onerror="this.style.display='none'" />`
                                                : '';
                                            el.innerHTML = `
                                                <div class="globe-marker-wrapper">
                                                    <div class="${tooltipCls}">
                                                        <div class="globe-marker-tooltip-header">
                                                            <span class="globe-marker-tooltip-online">Online</span>
                                                            ${avatarHtml}
                                                            <div class="globe-marker-tooltip-title">${esc(d.name)}</div>
                                                            <div class="globe-marker-tooltip-country">
                                                                ${flagHtml}
                                                                ${esc(d.country)}
                                                            </div>
                                                        </div>
                                                        <div class="globe-marker-tooltip-rows">
                                                            <div class="globe-marker-tooltip-row"><span class="globe-marker-tooltip-label">Current page</span><span class="globe-marker-tooltip-value">${esc(d.currentPage)}</span></div>
                                                            <div class="globe-marker-tooltip-row"><span class="globe-marker-tooltip-label">Last active</span><span class="globe-marker-tooltip-value">${esc(d.lastSeen)}</span></div>
                                                            <div class="globe-marker-tooltip-row"><span class="globe-marker-tooltip-label">Device</span><span class="globe-marker-tooltip-value">${esc(d.device)}</span></div>
                                                            <div class="globe-marker-tooltip-row"><span class="globe-marker-tooltip-label">Referrer</span><span class="globe-marker-tooltip-value">${esc(d.referrer)}</span></div>
                                                        </div>
                                                    </div>
                                                    ${markerAvatarHtml}
                                                    ${markerFlagHtml}
                                                </div>
                                            `;
                                        }
                                        el.style.pointerEvents = 'auto';
                                        el.style.cursor = 'pointer';
                                        return el;
                                    }}
                                    htmlTransitionDuration={0}
                                    enablePointerInteraction={true}
                                    width={window.innerWidth}
                                    height={window.innerHeight}
                                    onGlobeReady={() => {
                                        setGlobeLoading(false);
                                        if (!globeMapRef.current) return;
                                        const controls = globeMapRef.current.controls();
                                        controls.minDistance = 200;
                                        controls.maxDistance = 400;

                                        // Make canvas transparent so CSS space bg shows through
                                        try {
                                            const scene = globeMapRef.current.scene();
                                            if (scene) scene.background = null;
                                            const renderer = globeMapRef.current.renderer();
                                            if (renderer) {
                                                renderer.setClearColor(0x000000, 0);
                                                renderer.domElement.style.background = 'transparent';
                                            }
                                        } catch (_) { /* fallback: dark bg is fine */ }

                                        // ── Space parallax: poll camera every frame (no event dependency) ──
                                        const spaceBg = document.querySelector('.globe-space-bg');
                                        if (!spaceBg) return;
                                        const nebEl = spaceBg.querySelector('.globe-space-nebula');
                                        const farEl = spaceBg.querySelector('.globe-stars-far');
                                        const midEl = spaceBg.querySelector('.globe-stars-mid');
                                        const nearEl = spaceBg.querySelector('.globe-stars-near');

                                        // Record starting camera angles
                                        const cam = globeMapRef.current.camera();
                                        const p0 = cam.position;
                                        const initAz = Math.atan2(p0.x, p0.z);
                                        const initPo = Math.atan2(p0.y, Math.sqrt(p0.x * p0.x + p0.z * p0.z));

                                        let curX = 0, curY = 0;
                                        let rafId = null;
                                        let running = true;

                                        const tick = () => {
                                            if (!running) return;
                                            try {
                                                const p = cam.position;
                                                const az = Math.atan2(p.x, p.z);
                                                const po = Math.atan2(p.y, Math.sqrt(p.x * p.x + p.z * p.z));
                                                // Delta from initial position, normalized -1…1
                                                const tX = -((az - initAz) / Math.PI);
                                                const tY = (po - initPo) / (Math.PI * 0.5);
                                                // Fast lerp (0.15 = snappy, responsive follow)
                                                curX += (tX - curX) * 0.15;
                                                curY += (tY - curY) * 0.15;
                                                // Big multipliers for strong visible movement
                                                if (nebEl)  nebEl.style.transform  = `translate3d(${(curX * 40).toFixed(1)}px, ${(curY * 30).toFixed(1)}px, 0)`;
                                                if (farEl)  farEl.style.transform  = `translate3d(${(curX * 90).toFixed(1)}px, ${(curY * 70).toFixed(1)}px, 0)`;
                                                if (midEl)  midEl.style.transform  = `translate3d(${(curX * 160).toFixed(1)}px, ${(curY * 120).toFixed(1)}px, 0)`;
                                                if (nearEl) nearEl.style.transform = `translate3d(${(curX * 250).toFixed(1)}px, ${(curY * 190).toFixed(1)}px, 0)`;
                                            } catch (_) {}
                                            rafId = requestAnimationFrame(tick);
                                        };

                                        rafId = requestAnimationFrame(tick);

                                        // Store cleanup
                                        spaceParallaxRef.current = () => {
                                            running = false;
                                            if (rafId) cancelAnimationFrame(rafId);
                                        };
                                    }}
                                />
                            </div>

                            {/* Powered by badge */}
                            <a href="#" className="dashboard-globe-powered-by">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                                </svg>
                                <span>Powered by Optimus</span>
                            </a>
                        </div>
                    </div>,
                    document.body
                )}
            </main>
            </div>
        </div>
    );
}
